import "@babel/polyfill";
import { ENDPOINTS } from "../constants/endpoints";

let hasPose;

/**
 * Validates image content for professional appropriateness and safety.
 * Implements extremely strict filtering for nudity, suggestive attire, and provocative poses.
 */
export const validateImageSafety = async (apikey, image) => {
  let gemini_uri = ENDPOINTS.gemini.index.uri;

  let text;
  try {
    let formData = new FormData();

    formData.append("gemini_action", "Validate");
    formData.append("image_base64", image.base64);
    formData.append("mime_type", image.file.type);

    const res = await fetch(gemini_uri, {
      method: "POST",
      body: formData
    });

    // This handles standard non-200 server failures if they happen
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
    }

    // 1. CRITICAL: Unpack the JSON string stream exactly once
    const data = await res.json();

    // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
    if (data.error) {
      // Standardize the error message into a string variable
      const errMsg =
        typeof data.error === "object" ? data.error.message : data.error;
      // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

      // Track 1: Handle Google's 503 Overloaded Error
      if (
        errMsg.includes("overloaded") ||
        errMsg.includes("demand") ||
        errMsg.includes("503")
      ) {
        alert("MODEL_OVERLOADED! Please retry after sometime.");
        throw new Error("MODEL_OVERLOADED");
      }

      // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
      if (
        errMsg.includes("expired") ||
        errMsg.includes("timeout") ||
        errMsg.includes("504")
      ) {
        alert(
          "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
        );
        throw new Error("SERVER_TIMEOUT");
      }

      // Track 3: General Fallback for other errors
      alert(`Error Encountered: ${errMsg}`);
      throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
    }

    // 3. SUCCESS PATH: Return the clean data object safely to Promise.race
    if (data.candidates) {
      text = data.candidates[0].content.parts[0].text;
    } else text = "";
  } catch (err) {
    // alert("MODEL not available. Try Again.");

    if (err.status === 429)
      return {
        safe: true,
        reason: "quota exceeded"
      };
    if (err.status === 400)
      return {
        safe: true,
        reason: "INVALID KEY"
      };

    return {
      safe: true,
      reason: "INVALID KEY"
    };
  }

  text = text ? text.trim().toUpperCase() : "REJECT";
  const isSafe = text.startsWith("SAFE");

  return {
    safe: isSafe,
    reason: !isSafe
      ? `Content violation: ${
          text.replace("REJECT:", "").trim() ||
          "The image contains prohibited content (revealing attire, standalone blouse, or suggestive posing)."
        }`
      : undefined
  };
};

/**
 * Counts the number of human beings in the provided image whose faces are fully visible.
 */

export const countPeople = async (apikey, image) => {
  let gemini_uri = ENDPOINTS.gemini.index.uri;
  let text;

  try {
    let formData = new FormData();

    formData.append("gemini_action", "countPeople");
    formData.append("image_base64", image.base64);
    formData.append("mime_type", image.file.type);

    const res = await fetch(gemini_uri, {
      method: "POST",
      body: formData
    });

    // This handles standard non-200 server failures if they happen
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
    }

    // 1. CRITICAL: Unpack the JSON string stream exactly once
    const data = await res.json();

    // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
    if (data.error) {
      // Standardize the error message into a string variable
      const errMsg =
        typeof data.error === "object" ? data.error.message : data.error;
      // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

      // Track 1: Handle Google's 503 Overloaded Error
      if (
        errMsg.includes("overloaded") ||
        errMsg.includes("demand") ||
        errMsg.includes("503")
      ) {
        alert("MODEL_OVERLOADED! Please retry after sometime.");
        throw new Error("MODEL_OVERLOADED");
      }

      // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
      if (
        errMsg.includes("expired") ||
        errMsg.includes("timeout") ||
        errMsg.includes("504")
      ) {
        alert(
          "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
        );
        throw new Error("SERVER_TIMEOUT");
      }

      // Track 3: General Fallback for other errors
      alert(`Error Encountered: ${errMsg}`);
      throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
    }

    // 3. SUCCESS PATH: Return the clean data object safely to Promise.race
    if (data.candidates) {
      text = data.candidates[0].content.parts[0].text;
    } else text = -2;
  } catch (err) {
    // console.log(`countPeople Error: ${err}`);
    return -1;
  }

  const count = parseInt(text, 10);
  return isNaN(count) ? 0 : count;
};

/**
 * Detects the gender of the subject in the photo.
 */
export const detectGender = async (apikey, image) => {
  let gemini_uri = ENDPOINTS.gemini.index.uri;
  let text;

  try {
    let formData = new FormData();

    formData.append("gemini_action", "detectGender");
    formData.append("image_base64", image.base64);
    formData.append("mime_type", image.file.type);

    const res = await fetch(gemini_uri, {
      method: "POST",
      body: formData
    });

    // This handles standard non-200 server failures if they happen
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
    }

    // 1. CRITICAL: Unpack the JSON string stream exactly once
    const data = await res.json();

    // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
    if (data.error) {
      // Standardize the error message into a string variable
      const errMsg =
        typeof data.error === "object" ? data.error.message : data.error;
      // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

      // Track 1: Handle Google's 503 Overloaded Error
      if (
        errMsg.includes("overloaded") ||
        errMsg.includes("demand") ||
        errMsg.includes("503")
      ) {
        alert("MODEL_OVERLOADED! Please retry after sometime.");
        throw new Error("MODEL_OVERLOADED");
      }

      // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
      if (
        errMsg.includes("expired") ||
        errMsg.includes("timeout") ||
        errMsg.includes("504")
      ) {
        alert(
          "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
        );
        throw new Error("SERVER_TIMEOUT");
      }

      // Track 3: General Fallback for other errors
      alert(`Error Encountered: ${errMsg}`);
      throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
    }

    // 3. SUCCESS PATH: Return the clean data object safely to Promise.race
    if (data.candidates) {
      text = data.candidates[0].content.parts[0].text;
    } else text = "NA";
  } catch (err) {
    // alert("MODEL not available. Try Again.");
    return "NA";
  }

  text = text ? text.toLowerCase().trim() : "unknown";
  if (text.includes("male") && !text.includes("female")) return "male";
  if (text.includes("female")) return "female";
  return "unknown";
};

/**
 * Analyzes a group image to describe the arrangement and poses of people.
 */
// text: "Analyze the group arrangement and poses in this image with extreme anatomical precision. Identify each person using generic placeholders like 'Figure 1', 'Figure 2', etc. For each person, describe their exact spatial position (e.g., 'front row, second from left') and their precise physical pose. Focus on the specific positioning of all limbs: are legs crossed (and how?), where are the hands resting (on a lap, on another person's shoulder, at their side?), what is the tilt of the head and the angle of the torso? CRITICAL: You MUST NOT mention the age, gender, ethnicity, or any other identifying characteristics. Focus ONLY on the technical blueprint for replicating this exact composition. Use a numbered list format. For EACH figure detected, you MUST use the following EXACT format: 'X. Map **Figure X:** to the <brief position description> (Detailed anatomical description of pose)'. Example: '1. Map **Figure 1:** to the person sitting in the front row, center-left (Legs are crossed at the knee with the right leg over the left. Hands are clasped together resting on the upper knee. Torso is slightly angled towards the center.)'."

export const analyzeGroupArrangement = async (apikey, image) => {
  let gemini_uri = ENDPOINTS.gemini.index.uri;
  let text;

  try {
    let formData = new FormData();

    formData.append("gemini_action", "analyzeGroupArrangement");
    formData.append("image_base64", image.base64);
    formData.append("mime_type", image.file.type);

    const res = await fetch(gemini_uri, {
      method: "POST",
      body: formData
    });

    // This handles standard non-200 server failures if they happen
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
    }

    // 1. CRITICAL: Unpack the JSON string stream exactly once
    const data = await res.json();

    // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
    if (data.error) {
      // Standardize the error message into a string variable
      const errMsg =
        typeof data.error === "object" ? data.error.message : data.error;
      // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

      // Track 1: Handle Google's 503 Overloaded Error
      if (
        errMsg.includes("overloaded") ||
        errMsg.includes("demand") ||
        errMsg.includes("503")
      ) {
        alert("MODEL_OVERLOADED! Please retry after sometime.");
        throw new Error("MODEL_OVERLOADED");
      }

      // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
      if (
        errMsg.includes("expired") ||
        errMsg.includes("timeout") ||
        errMsg.includes("504")
      ) {
        alert(
          "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
        );
        throw new Error("SERVER_TIMEOUT");
      }

      // Track 3: General Fallback for other errors
      alert(`Error Encountered: ${errMsg}`);
      throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
    }

    // 3. SUCCESS PATH: Return the clean data object safely to Promise.race
    if (data.candidates) {
      text = data.candidates[0].content.parts[0].text;
    } else text = "";
  } catch (err) {
    alert("MODEL not available. Try Again.");
    throw new Error(err);
  }

  return text.trim() || "";
};

const extractNoImageReason = (response) => {
  if (!response) {
    return "No response was returned from the image generator model.";
  }

  // Log full response for debugging / visibility in server logs
  console.error(
    "Gemini Pro Image generator returned no image. Full response object:",
    JSON.stringify(response, null, 2)
  );

  const candidate = response.candidates[0];
  if (candidate) {
    const finishReason = candidate.finishReason;
    const finishMessage = candidate.finishMessage;
    const safetyRatings = candidate.safetyRatings || [];

    // Filter triggered safety ratings if any are MEDIUM, HIGH, VERY_LIKELY, LIKELY, or POSSIBLE
    const activeSafety = safetyRatings.filter(
      (r) =>
        r.probability &&
        ["MEDIUM", "HIGH", "VERY_LIKELY", "LIKELY", "POSSIBLE"].includes(
          r.probability.toUpperCase()
        )
    );

    if (finishReason && finishReason !== "STOP") {
      let msg = `Image generation failed. Model finish reason: "${finishReason}"`;
      if (finishMessage) {
        msg += ` (${finishMessage})`;
      }
      if (activeSafety.length > 0) {
        const ratingsStr = activeSafety
          .map((r) => `${r.category}: ${r.probability}`)
          .join(", ");
        msg += `. Triggered safety flags: [${ratingsStr}]`;
      } else if (safetyRatings.length > 0) {
        const allRatingsStr = safetyRatings
          .map((r) => `${r.category}: ${r.probability}`)
          .join(", ");
        msg += `. Safety details: [${allRatingsStr}]`;
      }
      return msg;
    }

    if (activeSafety.length > 0) {
      const ratingsStr = activeSafety
        .map((r) => `${r.category}: ${r.probability}`)
        .join(", ");
      return `Image generation was filtered by safety controls. Flagged safety categories: [${ratingsStr}]`;
    }
  }

  // Check prompt feedback
  const promptFeedback = response.promptFeedback;
  if (promptFeedback) {
    const blockReason = promptFeedback.blockReason;
    const blockMessage = promptFeedback.blockMessage;
    const safetyRatings = promptFeedback.safetyRatings || [];

    if (blockReason) {
      let msg = `The prompt was blocked by safety systems. Block reason: "${blockReason}"`;
      if (blockMessage) {
        msg += ` (${blockMessage})`;
      }
      const activeSafety = safetyRatings.filter(
        (r) =>
          r.probability &&
          ["MEDIUM", "HIGH", "VERY_LIKELY", "LIKELY", "POSSIBLE"].includes(
            r.probability.toUpperCase()
          )
      );
      if (activeSafety.length > 0) {
        const ratingsStr = activeSafety
          .map((r) => `${r.category}: ${r.probability}`)
          .join(", ");
        msg += `. Safety flags: [${ratingsStr}]`;
      }
      return msg;
    }
  }

  // Fallback check: maybe candidates array is empty
  if (!response.candidates || response.candidates.length === 0) {
    return "No candidates were returned in the response. This usually indicates that the prompt or safety policies completely blocked the response content.";
  }

  return "No image data was found in the candidate response. The generation may have been rejected by the model's safety and quality guardrails or returned an empty payload.";
};

/**
 * Classifies the semantic purpose of a snippet based on its instruction,
 * carefully parsing negation and exclusion constructs (e.g. "do not use pose", "ignore clothing").
 */
export const classifySnippetInstruction = (instruction) => {
  const normalized = (instruction || "").toLowerCase().trim();

  const poseKeywords = [
    "pose",
    "posture",
    "position",
    "gesture",
    "geometry",
    "composition",
    "stand",
    "standing",
    "sit",
    "sitting",
    "lean",
    "leaning",
    "riding",
    "driving",
    "cross",
    "crossing",
    "hand",
    "hands",
    "arm",
    "arms",
    "body",
    "stance",
    "orientation",
    "tilt",
    "angle"
  ];
  const dressKeywords = [
    "clothing",
    "attire",
    "dress",
    "suit",
    "garment",
    "blazer",
    "jacket",
    "outfit",
    "saree",
    "lehenga",
    "sari",
    "costume",
    "apparel",
    "clothes",
    "shirt",
    "pant",
    "pants",
    "skirt",
    "gown",
    "tuxedo",
    "uniform",
    "fabric",
    "textile",
    "coat",
    "tie",
    "trouser"
  ];
  const bgKeywords = [
    "background",
    "scenery",
    "backdrop",
    "environment",
    "setting",
    "scenic",
    "location",
    "landscape",
    "venue",
    "lawn",
    "garden",
    "room",
    "beach",
    "office",
    "outdoors",
    "indoors"
  ];

  // Default flags based on simple occurrence mapping
  hasPose = poseKeywords.some((kw) => normalized.includes(kw));
  let hasDress = dressKeywords.some((kw) => normalized.includes(kw));
  let hasBg = bgKeywords.some((kw) => normalized.includes(kw));

  // If no instruction is supplied, default to attire (the primary purpose of Style Builder)
  if (!normalized) {
    return { hasPose: false, hasDress: true, hasBg: false };
  }

  // If multiple aspects are detected, let's look closer at negations and exclusives
  if (hasPose || hasDress || hasBg) {
    // 1. Check for strong "ONLY" / "JUST" restrictions
    const hasOnlyPose =
      /\b(only|just|strictly)\b.*?\b(pose|posture|position|gesture|composition|riding|stance)\b/i.test(
        normalized
      ) ||
      /\b(pose|posture|position|gesture|composition|riding|stance)\b.*?\b(only|just|strictly)\b/i.test(
        normalized
      );
    const hasOnlyDress =
      /\b(only|just|strictly)\b.*?\b(clothing|attire|dress|suit|garment|wear|outfit|saree|sari|lehenga|bag|accessory|prop|shirt|pants|skirt|costume)\b/i.test(
        normalized
      ) ||
      /\b(clothing|attire|dress|suit|garment|wear|outfit|saree|sari|lehenga|bag|accessory|prop|shirt|pants|skirt|costume)\b.*?\b(only|just|strictly)\b/i.test(
        normalized
      );
    const hasOnlyBg =
      /\b(only|just|strictly)\b.*?\b(background|scenery|backdrop|environment|setting|location|landscape)\b/i.test(
        normalized
      ) ||
      /\b(background|scenery|backdrop|environment|setting|location|landscape)\b.*?\b(only|just|strictly)\b/i.test(
        normalized
      );

    if (hasOnlyPose && !hasOnlyDress && !hasOnlyBg) {
      return { hasPose: true, hasDress: false, hasBg: false };
    }
    if (hasOnlyDress && !hasOnlyPose && !hasOnlyBg) {
      return { hasPose: false, hasDress: true, hasBg: false };
    }
    if (hasOnlyBg && !hasOnlyPose && !hasOnlyDress) {
      return { hasPose: false, hasDress: false, hasBg: true };
    }

    // 2. Clause/Fragment Analysis for Negation or Exclusions
    // Split on punctuation, coordinate conjunctions ("but", "except"), and "and"
    const clauses = normalized.split(
      /(?:,|\.|\bbut\b|\bexcept\b|;|:|\band\b)/i
    );

    let poseNegated = false;
    let dressNegated = false;
    let bgNegated = false;

    let poseAffirmed = false;
    let dressAffirmed = false;
    let bgAffirmed = false;

    for (const clause of clauses) {
      const trimmedClause = clause.trim();
      if (!trimmedClause) continue;

      // Check if this clause contains static negation markers
      const hasNegation =
        /\b(do not|don'?t|not|no|ignore|avoid|without|exclude|except|donot)\b/i.test(
          trimmedClause
        );

      const mentionsPose = poseKeywords.some((kw) =>
        trimmedClause.includes(kw)
      );
      const mentionsDress = dressKeywords.some((kw) =>
        trimmedClause.includes(kw)
      );
      const mentionsBg = bgKeywords.some((kw) => trimmedClause.includes(kw));

      if (hasNegation) {
        if (mentionsPose) poseNegated = true;
        if (mentionsDress) dressNegated = true;
        if (mentionsBg) bgNegated = true;
      } else {
        if (mentionsPose) poseAffirmed = true;
        if (mentionsDress) dressAffirmed = true;
        if (mentionsBg) bgAffirmed = true;
      }
    }

    // Use regex fallback for check if the negation is attached directly to keywords even in a non-split string (no punctuation)
    // E.g. "replicate this dress do not use pose" (no commas or periods)
    if (!poseNegated) {
      poseNegated =
        /\b(do not|don'?t|not|no|ignore|avoid|without|exclude|except|donot)\b\s*(?:[\w\s]{0,25})\b(pose|posture|position|gesture|geometry|composition|stand|sit|lean|riding|stance)\b/i.test(
          normalized
        );
    }
    if (!dressNegated) {
      dressNegated =
        /\b(do not|don'?t|not|no|ignore|avoid|without|exclude|except|donot)\b\s*(?:[\w\s]{0,25})\b(clothing|attire|dress|suit|garment|wear|blazer|jacket|outfit|saree|lehenga|sari|bag|accessory|accouterment|jewelry|necklace|earring|bangle|crown|cap|parasol|umbrella|prop|shirt|pants|skirt)\b/i.test(
          normalized
        );
    }
    if (!bgNegated) {
      bgNegated =
        /\b(do not|don'?t|not|no|ignore|avoid|without|exclude|except|donot)\b\s*(?:[\w\s]{0,25})\b(background|scenery|backdrop|environment|setting|scenic|location)\b/i.test(
          normalized
        );
    }

    // Apply negation logic
    if (poseNegated) hasPose = false;
    if (dressNegated) hasDress = false;
    if (bgNegated) hasBg = false;

    // Safety fallback: if everything is false, restore those that were positively affirmed
    if (!hasPose && !hasDress && !hasBg) {
      hasPose = poseAffirmed;
      hasDress = dressAffirmed;
      hasBg = bgAffirmed;
    }
  } else {
    // If no keyword matches at all, default to attire
    hasDress = true;
  }

  return { hasPose, hasDress, hasBg };
};

export const generateProfessionalHeadshot = async (
  apikey,
  is4K,
  mainImage,
  background,
  options,
  detectedGender,
  maleRef,
  femaleRef,
  bgRef,
  snippets
) => {
  // Dynamically analyze snippets and classify their purposes across up to MAX_SNIPPETS = 10
  const poseSnippetIndexes = [];
  const dressSnippetIndexes = [];
  const bgSnippetIndexes = [];

  (snippets || []).forEach((s, idx) => {
    const classification = classifySnippetInstruction(s.instruction || "");
    if (classification.hasPose) poseSnippetIndexes.push(idx + 1);
    if (classification.hasDress) dressSnippetIndexes.push(idx + 1);
    if (classification.hasBg) bgSnippetIndexes.push(idx + 1);
  });

  // Construct a text block for individual style snippets if they exist
  let snippetsInstructionBlock = "";
  if (snippets && snippets.length > 0) {
    const designations = snippets.map((s, idx) => {
      const isPose = poseSnippetIndexes.includes(idx + 1);
      const isDress = dressSnippetIndexes.includes(idx + 1);
      const isBg = bgSnippetIndexes.includes(idx + 1);

      let typeDesc = "GENERAL REFERENCE";
      const tags = [];
      if (isPose) tags.push("POSE/POSTURE");
      if (isDress) tags.push("CLOTHING/ATTIRE");
      if (isBg) tags.push("BACKGROUND/ENVIRONMENT");

      if (tags.length === 1) {
        if (isPose)
          typeDesc =
            "STRICTLY POSE/POSTURE ONLY (IGNORE ITS CLOTHING/SUIT/DRESS AND BACKGROUND COMPLETELY)";
        else if (isDress)
          typeDesc =
            "STRICTLY CLOTHING/ATTIRE/DRESS/JEWELRY ONLY (IGNORE ITS BODY POSE/POSITION AND BACKGROUND COMPLETELY)";
        else if (isBg)
          typeDesc =
            "STRICTLY BACKGROUND/ENVIRONMENT ONLY (IGNORE ITS BODY POSE, POSITION, AND CLOTHING COMPLETELY)";
      } else if (tags.length > 1) {
        typeDesc = `COMBINED ${tags.join(
          " & "
        )} REFERENCE (Use only the specified ${tags.join(
          ", and "
        )} elements from this snippet and ignore others)`;
      }

      return `  - PANEL ${idx + 1} (SOURCE BLUEPRINT: SNIPPET ${idx + 1}):
    Specific instruction: "${
      s.instruction ||
      (isDress
        ? "Replicate exact attire shown."
        : isPose
        ? "Replicate exact pose shown."
        : "Replicate exact style shown.")
    }"
    DESIGNATION: ${typeDesc}`;
    });

    snippetsInstructionBlock = `\n  INDIVIDUAL SNIPPETS DIRECTIVES (MANDATORY BLUEPRINT SEPARATION):
  The provided TECHNICAL BLUEPRINT reference board contains the following individual snippets in sequence (from left to right, top to bottom):
  ${designations.join("\n")}\n`;
  }

  // 1. Background Environment Logic
  let backgroundInstruction = "";
  const blurDepth = background.blurDepth || 50;
  let blurDescription = blurDepth > 50 ? "pronounced" : "subtle";
  const blurSuffix = background.blur
    ? ` Apply a professional shallow depth of field with a ${blurDescription} bokeh blur to the background area.`
    : " Maintain sharp focus throughout the environment.";

  if (bgRef) {
    backgroundInstruction = `SCENE ANCHOR & LIGHTING INTEGRATION: 
    - Use the provided BACKGROUND reference catalogue image as the absolute master for spatial geometry, perspective, and global illumination.
    - COMPOSITION: Compose and frame the shoot so the subject and all associated items (like vehicles, props, or furniture) are physically present WITHIN the environment, not just layered on top.
    - LIGHTING GEL: The subject's lighting must perfectly match the direction, color temperature, and intensity of the environment's light sources. Apply realistic contact shadows, ambient occlusion, and reflections where the subject meets the environment.
    - SCALING: Scale the subject and items realistically to fit the perspective of the background.
    - INTEGRATION: The final image must look like a single, unedited RAW photograph taken with a high-end camera.`;
  } else if (background.type === "none") {
    backgroundInstruction = `ENVIRONMENT PRESERVATION: Strictly maintain the ORIGINAL background from Image-1. Do not replace the scenery or context with a studio backdrop. ${
      background.blur
        ? blurSuffix
        : "Retain all spatial details and the original sharpness of the existing background exactly."
    }`;
  } else if (background.type === "reference") {
    if (bgSnippetIndexes.length > 0) {
      backgroundInstruction = `ENVIRONMENT & SCENERY REPLICATION (STRICT OVERRIDE):
    - Identify the specific snippet(s) designated for background/environment/scenery: SNIPPET ${bgSnippetIndexes.join(
      " and SNIPPET "
    )}.
    - Use EXACTLY the background scene, greenery, lawn, outdoor setting, or indoor setting shown in SNIPPET ${bgSnippetIndexes.join(
      " and SNIPPET "
    )}.
    - Under no circumstances should you use the background from any other snippet (such as the tennis court from any dress snippet).
    - The subject and any elements (such as vehicles, bikes, or props mentioned) MUST be realistically placed and integrated directly on/within the background environment from the designated background snippet, with matching perspective, scale, and lighting.`;
    } else {
      backgroundInstruction = `ENVIRONMENT: Use the specific background environment described in the text directive or replication directive in REFERENCE CATALOGUE IMAGE. Ensure the subject in Image-1 is realistically integrated into this setting with matching lighting and perspective.`;
    }
  } else {
    backgroundInstruction =
      "ENVIRONMENT: Place the subject in a professional studio setting.";
    if (background.type === "clean") {
      backgroundInstruction = `ENVIRONMENT: Place the subject against a solid studio cyclorama background of color ${
        background.color || "#f0f0f0"
      }.${blurSuffix}`;
    } else if (background.type === "office") {
      backgroundInstruction = `ENVIRONMENT: Place the subject in a modern corporate office interior with natural window lighting.${blurSuffix}`;
    } else if (background.type === "retro") {
      backgroundInstruction = `ENVIRONMENT: Place the subject against a classic hand-painted canvas studio backdrop in ${
        background.color || "#5d4037"
      }.${blurSuffix}`;
    } else if (background.type === "gradient") {
      backgroundInstruction = `ENVIRONMENT: Place the subject against a smooth ${background.gradientStyle} studio gradient wall.${blurSuffix}`;
    }
  }

  // Posture Logic
  const targetRef = detectedGender === "male" ? maleRef : femaleRef;
  const hasPoseSnippet = poseSnippetIndexes.length > 0;
  const hasDressSnippet = dressSnippetIndexes.length > 0;
  const hasBgSnippet = bgSnippetIndexes.length > 0;

  const hasPose = hasPoseSnippet || options.posture === "clone";
  const hasDress =
    hasDressSnippet ||
    (!!targetRef &&
      (snippets && snippets.length > 0
        ? dressSnippetIndexes.length > 0
        : options.attireMode !== "keepOriginal"));
  const hasBg = hasBgSnippet || !!bgRef || background.type === "reference";

  const poseSourceDesc =
    poseSnippetIndexes.length > 0
      ? `Snippet ${poseSnippetIndexes.join(" and Snippet ")}`
      : "the designated POSE/POSTURE snippet";

  const dressSourceDesc =
    dressSnippetIndexes.length > 0
      ? `Snippet ${dressSnippetIndexes.join(" and Snippet ")}`
      : "the designated ATTIRE/CLOTHING/DRESS snippet";

  const bgSourceDesc =
    bgSnippetIndexes.length > 0
      ? `Snippet ${bgSnippetIndexes.join(" and Snippet ")}`
      : "the designated BACKGROUND/ENVIRONMENT snippet";

  let postureInstruction = "";
  if (hasPose) {
    postureInstruction = `ANATOMICAL CROSS-OVER & CONTRALATERAL PROTOCOL (MANDATORY):
    - POSE REFERENCE ANCHOR: You MUST adopt the exact body posture from ${poseSourceDesc} on the TECHNICAL BLUEPRINT board with 100% fidelity.
    - IDENTIFY LIMB ORIGIN: Determine if the reaching arm starts at the Right or Left shoulder.
    - IDENTIFY TARGET SIDE: Determine if the hand touches the IPSILATERAL (same side as shoulder) or CONTRALATERAL (opposite side of shoulder) side of the face/head.
    - CROSS-BODY INTEGRITY: If the reference shows a CONTRALATERAL touch (e.g., Right hand touching Left ear), you MUST replicate the arm crossing the center of the body. Reaching for the same-side ear when the reference reaches for the opposite-side ear is a "Midline Violation" and a failure of the protocol.
    - POSE ACCURACY: Replicate the exact limb angles, joint rotations, and micro-gestures from ${poseSourceDesc} with 100% fidelity.
    ${
      hasDress
        ? `- STRICT MULTI-SNIPPET POSE ISOLATION: The technical blueprint board contains multiple panels/snippets. You MUST adopt the exact pose and posture shown in ${poseSourceDesc} only. Under no circumstances should you adopt the posture of any person shown in the ATTIRE, DRESS, or CLOTHING snippets. The final subject's body posture and limb placements must come 100% from ${poseSourceDesc}.`
        : ""
    }`;
  } else if (options.posture === "original") {
    postureInstruction =
      "POSE FIDELITY: Maintain the EXACT posture and body orientation of the subject from Image-1. Do not shift the angle of the shoulders or the tilt of the head.";
  } else if (options.posture === "professional") {
    postureInstruction =
      "POSE (PROFESSIONAL): Position the subject in a classic, confident professional portrait pose with no folded hands. (e.g., slightly angled to the camera, shoulders squared yet relaxed).";
  } else if (options.posture === "casual") {
    postureInstruction =
      "POSE (CASUAL): Position the subject in a natural, relaxed, and approachable casual post.";
  }

  // 2. HARDCORE BIOMETRIC LOCK
  const totalPhysicalIdentityInstruction = `
  CRITICAL BIOMETRIC AUTHENTICITY PROTOCOL:
  The resulting person MUST be the EXACT person from Image-1. This is a biometric recreation, not a generic likeness.
  1. FULL SUBJECT REGENERATION (ABSOLUTE): You must regenerate the entire person (face, neck, torso, limbs, and hands) as a 1:1 biometric match of Image-1 unless you are transforming the subject as per pose directive. Do NOT use the body frame, limbs, hands, or skin of the model in the reference snippet image, nor any of their age-related body characteristics. This must be a full 3D regeneration of the subject, NOT a low-effort face-swap. You are FORBIDDEN from pasting the subject's face onto the reference model's body.
  2. GENDER-APPROPRIATE ATTIRE (STRICT & MANDATORY): The subject in Image-1 is identified and classified as strictly ${
    detectedGender === "male" ? "MALE (Boy / Man)" : "FEMALE (Girl / Woman)"
  }. Because of this, you are STRICTLY FORBIDDEN from applying ${
    detectedGender === "male"
      ? "female-specific attire (such as dresses, skirts, gowns, blouses, saree, lehenga, or women's athletic skirts)"
      : "male-specific attire or ill-fitting masculine garments"
  } to the subject, regardless of compiling references, mappings, or replication directives.
  ${
    detectedGender === "male"
      ? "Since the subject is MALE, if the reference model is female and wears a dress, skirt, saree, or tennis skirt, you MUST adapt and translate that garment completely: instead of a dress/skirt/saree, render him in a high-quality, professional male equivalent outfit (such as trousers, a handsome shirt, clean polo/t-shirt, sweater, or blazer/suit jacket) that precisely mirrors the reference attire's color theme, fabrics, textures, patterns, and level of formality. For example, if the reference is a white tennis dress/skirt, render him in a white male tennis/polo shirt and white pants of identical color and texture."
      : "Since the subject is FEMALE, if the reference model is male wearing male-specific attire, you MUST adapt and translate that garment completely: instead of male-specific pants/jackets, render her in an equivalent highly professional and styled female outfit (such as a dress, pantsuit, skirt, or blouse) matching the color palette, quality, fabrics, and formality of the reference."
  }
  3. HERITAGE & ETHNICITY ANCHOR: Analyze and preserve the specific ethnic heritage, skin details, complexion, and bone structure of the subject from Image-1. Do not genericize, whitewash, or alter their ethnic features.
  4. BIOMETRIC LOCK (ABSOLUTE): Preserve the exact facial details, eyes, nose, mouth, wrinkles, age markers, bone structure, and unique facial markers from Image-1 with 100% fidelity unless you are transforming the subject as per pose directive. There must be zero identity drift unless you are transforming the subject as per pose directive.
  5. SKIN TONE ANCHOR: Every square inch of visible skin—including hands, neck, chest, and limbs—must perfectly match the ethnicity, skin tone, complexion, wrinkles, age, and texture of the subject in Image-1. Do not create a 'Frankenstein' effect by mixing the subject's face with the reference model's hands, neck, or skin.
  6. EXPRESSION LOCK: ${
    options.expression === "original"
      ? "MANDATORY: Maintain the EXACT facial expression from Image-1. Do NOT add a smile if they are not smiling. Do NOT change the position of the mouth or the tension in the eyes. The momentary facial emotion must be identical."
      : `Apply a ${
          options.expression === "smile"
            ? "warm studio smile"
            : "confident neutral expression"
        } while strictly preserving the underlying bone structure and eye shape of the person in Image-1.`
  }
  7. POSTURE & SPATIAL POSITIONS: Follow the designated posture directive with exact anatomical and limb placement precision.
  8. PHYSIQUE & BUILD LOCK (ABSOLUTE): Maintain the subject in Image-1's EXACT body type, weight, age and physical frame. If the subject has a larger build, they must remain large in the output. If they have a specific neck thickness or shoulder width, it must be preserved. Do NOT 'slim down' or alter the subject's natural proportions, age, or weight to match the reference style model.
  9. ANATOMICAL ISOLATION (IDENTITY ONLY): You are FORBIDDEN from using any body parts (hands, fingers, neck, legs, skin) or the body build (weight, frame, slimness/thickness) from the style reference. However, you MUST follow the SPATIAL POSITIONS of these limbs as defined in the reference.
  10. IDENTITY THEFT PREVENTION: You are FORBIDDEN from using any facial features, hair, expression, body characteristics, or skin/age characteristics from the "AESTHETIC & ATTIRE STYLE REFERENCE" or the dress reference snippet for Image-1 (Subject).
  11. NO FACE SWAP: Avoid doing a simple, low-effort 'face swap' where the subject's face is pasted onto the reference model's body, limbs, and skin tone. This causes age, body build, and ethnicity mismatch. You must perform a FULL subject in Image-1 regeneration.
  12. SPATIAL ORIENTATION LOCK: DO NOT mirror, flip, or invert the subject unless you are transforming the subject as per pose directive.. Maintain absolute spatial fidelity. When replicating pose, match the face orientation, head tilt, and direction of gaze directly from the reference image without mirroring.
  13. ZERO DEVIATION: You are strictly forbidden from modifying the subject's facial or bodily identity even minutely unless you are transforming the subject as per pose directive. Any deviation from the subject in image-1's physical features is a failure of the protocol.
  14. LIMB & HAND BIOLOGICAL PARITY: When hallucinating limbs or hands to match a pose, you MUST match the specific biological age and skin characteristics of the subject's face from Image-1. If the subject in Image-1 is an older adult, the hands MUST show realistic age-appropriate details (natural skin texture, visible tendons, veins, or subtle character lines). You are STRICTLY FORBIDDEN from rendering 'mannequin' hands or unnaturally smooth limbs that look like they belong to a younger person or the reference catalogue model. The limbs must be a perfect biological match to the subject's face in Image-1.`;

  // Construct Manual Attire Instruction
  let attireDetail = "";
  if (options.attireMode === "professional") {
    const isFemale = detectedGender === "female";
    let outerwear = "";

    const accessories = options.hasPocketSquare
      ? ` and a ${options.pocketSquareColor} pocket square`
      : "";

    if (options.attireType === "suit") {
      outerwear = `business suit jacket in color ${options.attireColor}${accessories}`;
    } else if (options.attireType === "blazer") {
      outerwear = `${options.blazerStyle} blazer in color ${options.attireColor}${accessories}`;
    } else if (options.attireType === "three-piece") {
      outerwear = `three-piece suit consisting of a ${options.attireColor} jacket and trousers, with a distinctive ${options.vestColor} vest/waistcoat${accessories}`;
    } else if (options.attireType === "tuxedo") {
      outerwear = `formal tuxedo in color ${options.attireColor} with ${
        options.lapelType
      } lapels${
        options.cummerbundColor
          ? ", and a " + options.cummerbundColor + " cummerbund"
          : ""
      }${accessories}`;
    }

    const innerwear = isFemale
      ? options.blouseStyle === "shirt-tie"
        ? `a ${options.shirtColor} shirt with a ${options.tieColor} tie`
        : `a ${options.blouseStyle} blouse in ${options.blouseColor}`
      : `a ${options.shirtStyle} shirt in ${options.shirtColor}${
          options.hasBowTie
            ? ` with a ${options.bowTieColor} bow tie`
            : options.hasTie
            ? ` with a ${options.tieStyle} ${options.tieColor} tie`
            : ""
        }`;

    attireDetail = `ATTIRE (PROFESSIONAL): Replace current clothing with a professional ${outerwear}. Underneath, the subject must wear ${innerwear}. The clothing must be high-quality, tailored, and perfectly fitted.`;
  } else if (options.attireMode === "graduation") {
    attireDetail = `ATTIRE (GRADUATION): Replace current clothing with a high-quality ${
      options.gradStyle
    } graduation gown in ${
      options.gownColor
    }. Subject must wear a matching mortarboard/cap in ${
      options.capColor
    } with a ${options.tasselColor} tassel on the ${options.tasselSide}. ${
      options.hasHood ? `Include an academic hood in ${options.hoodColor}.` : ""
    } ${options.hasStole ? `Include a stole in ${options.stoleColor}.` : ""} ${
      options.hasSash ? `Include a sash in ${options.sashColor}.` : ""
    } ${
      options.hasHonorCord
        ? `Include honor cords for ${options.honorSociety} in ${options.honorCordColor}.`
        : ""
    } ${
      options.gradProp !== "none"
        ? `Subject is ${
            options.gradProp === "scroll"
              ? "holding a diploma scroll tied with a " +
                options.scrollRibbonColor +
                " ribbon"
              : "holding/touching their graduation cap"
          }.`
        : ""
    }`;
  } else if (options.attireMode === "uniform") {
    attireDetail = `ATTIRE (UNIFORM): Replace current clothing with a ${
      options.uniformType
    } uniform in color ${options.uniformColor}. ${
      options.uniformType === "striped"
        ? `The uniform features ${options.uniformStripeWidth} stripes in ${options.uniformStripeColor}.`
        : ""
    } ${
      options.hasUniformTie
        ? `Include a matching uniform tie in ${options.uniformTieColor}.`
        : ""
    } ${
      options.uniformType === "pinafore"
        ? `The subject wears a ${options.pinaforeShirtSleeves}-sleeve ${options.pinaforeShirtPattern} blouse in ${options.pinaforeShirtColor} under the pinafore.`
        : ""
    }`;
  } else if (options.attireMode === "casual") {
    attireDetail = `ATTIRE (CASUAL): Replace current clothing with a casual ${
      options.casualTopType
    } in ${options.casualTopColor}. ${
      options.casualOuterType !== "none"
        ? `Layered with a ${options.casualOuterType} in ${
            options.casualOuterColor
          }${
            options.casualOuterType === "sweater"
              ? " (" + options.casualSweaterStyle + ")"
              : ""
          }.`
        : ""
    }`;
  } else {
    attireDetail = `ATTIRE (KEEP ORIGINAL): Maintain the exact clothing from Image-1. Only improve lighting and studio integration.`;
  }

  // 3. PIXEL-PERFECT REPLICATION DIRECTIVE
  let sceneDirective = "";

  if (targetRef) {
    sceneDirective = `DIGITAL TWIN REPLICATION PROTOCOL:
    Analyze the provided REFERENCE CATALOGUE. It is a structured technical composite board/blueprint made of designated "SOURCE BLUEPRINT" panels (e.g., SNIPPET 1, SNIPPET 2, etc.) with explicit labels below each panel defining their purpose.

    ${snippetsInstructionBlock}

    CRITICAL MULTI-SNIPPET ROLE DISPATCH & ISOLATION:
    ${
      hasPose
        ? `[ROLE: POSE & BODY SKELETON] -> Source: ${poseSourceDesc} on the TECHNICAL BLUEPRINT board:
    - You MUST clone the exact body posture, seating/standing stance, shoulder angle, arm positioning, elbow bend, wrist angles, hand touch/placement, and head tilt exclusively from ${poseSourceDesc}.
    - DISCARD ATTIRE FROM POSE REFERENCE: The person in ${poseSourceDesc} is strictly an anatomical mannequin. Under NO circumstances should you replicate their clothing, garments, or jewelry. Completely discard the attire shown on the pose model.`
        : ""
    }
    ${
      hasDress
        ? `[ROLE: ATTIRE & ACCESSORIES] -> Source: ${dressSourceDesc} on the TECHNICAL BLUEPRINT board:
    - You MUST replicate the exact clothing colors, patterns, fabric textures, materials, and jewelry from ${dressSourceDesc}.
    - DISCARD POSTURE FROM ATTIRE REFERENCE: Under NO circumstances should you replicate the body pose, arm position, or stance of the model in ${dressSourceDesc}. Drape this attire onto the pose skeleton defined above.`
        : ""
    }
    ${
      hasBg
        ? `[ROLE: BACKGROUND & ENVIRONMENT] -> Source: ${bgSourceDesc} on the TECHNICAL BLUEPRINT board:
    - The environment/background scenery must come exclusively from ${bgSourceDesc}. Under no circumstances should you use the background from any other snippet.`
        : ""
    }

    CROSS-CONTAMINATION FIREWALL (STRICT):
    - Absolute role separation: Pose and limbs come ONLY from ${
      hasPose ? `${poseSourceDesc}` : "Image-1/options"
    }. Attire and accessories come ONLY from ${
      hasDress ? `${dressSourceDesc}` : "configured attire options"
    }. Background comes ONLY from ${
      hasBg ? `${bgSourceDesc}` : "configured background options"
    }.
    - The models in the reference blueprints are 100% anonymous mannequins. You are strictly forbidden from using their faces, skin, hair, age, or physical identity.

    EXECUTION DIRECTIVES:
    ${
      hasPose
        ? postureInstruction
        : "- NATURAL POSE: Maintain natural posture per the posture directive without copying poses from attire references."
    }
    ${
      hasDress
        ? `- ATTIRE CLONING & GENDER ADAPTATION:
       - Visually analyze and replicate the garments, fabrics, textures, and accessories from ${dressSourceDesc} with high fidelity.
       - GENDER LOCK: Target subject is strictly ${
         detectedGender === "male"
           ? "MALE (Boy / Man)"
           : "FEMALE (Girl / Woman)"
       }. If the reference model in ${dressSourceDesc} is the opposite gender, adapt and translate the clothing into an equivalent ${
            detectedGender === "male"
              ? "male outfit (e.g., trousers, shirt/t-shirt, polo, or blazer/suit jacket)"
              : "female outfit (e.g., dress, skirt, blouse, or pantsuit)"
          } of identical color, fabric texture, and formality.
       - TAILORED FIT: Tailor the attire from ${dressSourceDesc} to fit the subject's original physical build from Image-1.
       - CULTURAL ATTIRE INTEGRITY: If ${dressSourceDesc} shows a cultural blouse (such as a saree blouse), ensure the final render includes the full professional attire (saree/lehenga) with modest midriff/navel coverage; never render a standalone blouse.`
        : "- ATTIRE SPECIFICATION: Maintain attire according to the selected options."
    }
    - BIOMETRIC IDENTITY LOCK:
       - 100% facial identity, age, bone structure, eye shape, smile, and ethnic heritage from Image-1. Zero face swapping; full biometric regeneration.
       - All visible skin (hands, neck, limbs) must match Image-1 in skin tone, age, and natural skin texture.`;
  } else {
    sceneDirective = `ATTIRE TRANSFORMATION PROTOCOL:
    ${attireDetail}
    - IDENTITY LOCK: ${totalPhysicalIdentityInstruction}`;
  }

  // 4. Facial Characteristics & Grooming
  const facialHairLogic =
    options.facialHair === "keep"
      ? "Preserve original facial hair features exactly."
      : options.facialHair === "shaven"
      ? options.keepMustache
        ? "Maintain original mustache but render the jawline as clean-shaven."
        : "Render the subject with a perfectly clean-shaven appearance."
      : options.facialHair === "clone" && maleRef
      ? "Clone the exact facial hair style (beard, mustache, stubble) from the provided REFERENCE CATALOGUE IMAGE."
      : "Maintain original professional grooming standards.";

  let hairLogic = "";
  if (options.hairStyle === "keep") {
    hairLogic =
      "HAIR FIDELITY: Lock the hairstyle, hairline, and volume exactly as seen in Image-1. Maintain the original orientation.";
  } else if (options.hairStyle === "groom") {
    hairLogic =
      "HAIR GROOMING: Maintain original hair length/color but render in a perfectly groomed professional style.";
  } else if (options.hairStyle === "match-ref" && targetRef) {
    hairLogic =
      "HAIR MATCHING: Adopt the hairstyle/cut from the REFERENCE CATALOGUE IMAGE while maintaining the subject in Image-1's ORIGINAL hair color.";
  } else {
    hairLogic = "Preserve original hair characteristics and color.";
  }

  // EYELOCK Instruction setup based on facingCamera and posture
  let eyelockInstruction = "";
  if (options.facingCamera) {
    eyelockInstruction =
      "The subject's face MUST be oriented fully forward, looking directly into the camera lens with confident eye contact.";
  } else {
    if (options.posture === "clone" && poseSnippetIndexes.length > 0) {
      eyelockInstruction = `The subject's head rotation, facial angle, and eye gaze direction MUST strictly clone and match the pose reference snippet (Snippet ${poseSnippetIndexes.join(
        ", "
      )}). If the template model in that pose snippet is facing away, has her head turned in a 3/4 profile, or is gazing off to the side/downwards, the subject in the output image MUST turn their head, show that profile/facing angle, and look in that same direction, while keeping the subject's exact biological face, eyes, nose, mouth, and features from Image-1 completely intact and unchanged. Do NOT force them to look at the camera, but preserve 100% of Image-1's biometric identity.`;
    } else {
      eyelockInstruction =
        "Maintain the exact head orientation, face angle, and gaze direction seen in Image-1.";
    }
  }

  const featuresInstruction = `SUBJECT FIDELITY:
    - IDENTITY LOCK: ${totalPhysicalIdentityInstruction}
    - HAIR: ${hairLogic}
    - SKIN: ${
      options.noBlemishes
        ? "Professional retouching with silky smooth skin texture."
        : "Preserve natural skin details but ensure no greasy sheen."
    }
    - GROOMING: ${facialHairLogic}
    - EYELOCK: ${eyelockInstruction}`;

  // 5. Composition & Rendering
  let framingBase = "Classic chest-up framing.";
  if (options.aspectRatio === "9:16") {
    framingBase =
      "MANDATORY FULL-LENGTH PORTRAIT: You MUST render the entire body from HEAD-TO-TOE including feet. Even if Image-1 is a closeup, cropped, or half-length photo, you MUST hallucinate and generate the lower body, legs, and shoes in a professional manner that matches the chosen attire. The entire human silhouette MUST be visible from top to bottom within the 9:16 frame.";
  } else if (options.isCloseup) {
    framingBase = `Professional tight closeup (Intensity: ${options.closeupIntensity}/100).`;
  }

  // Lighting Instruction refinement
  const lightingDirective =
    options.lightingMode === "even"
      ? `LIGHTING PROTOCOL (EVEN & DIFFUSED): 
       - Use large studio softboxes for wrap-around, even lighting.
       - STRICTLY FORBIDDEN: Harsh specular highlights, blown-out white hotspots on the forehead or cheeks, and hard-edged shadows.
       - The face must be bright, clear, and well-illuminated, but with a soft, matte finish on the skin.
       - Ensure a professional balance of light that is flattering and avoids oily-looking reflections.`
      : `LIGHTING PROTOCOL (DRAMATIC & CINEMATIC): 
       - Use high-contrast studio lighting with a strong key light and deep, moody shadows (Chiaroscuro style).
       - Incorporate subtle rim lighting to separate the subject from the background.
       - Emphasize facial contours and textures for a cinematic, high-end editorial look.
       - Avoid flat illumination; embrace the play of light and shadow for depth and drama.`;

  const photographicExcellence = `PHOTOGRAPHIC EXCELLENCE:
    - Render with stunning clarity and detail using the characteristics of a high-end professional camera.
    - Ensure pixel-perfect sharpness on the subject's eyes and features.
    - Apply natural grain and realistic lens characteristics.
    - STUDIO EQUIPMENT PROHIBITION (STRICT): You are STRICTLY FORBIDDEN from including any studio equipment, lighting gear, or photography tools in the final output. DO NOT RENDER: softboxes, light stands, strobes, umbrellas, reflectors, backdrop stands, backdrop stand legs, power cables, or cameras. The background must be a clean, unobstructed professional setting. Any visible gear is a direct violation.`;

  const compositionDirective = `COMPOSITION & RENDERING:
    - FRAMING: ${framingBase}
    - POSE: ${(() => {
      let basePose = "";
      if (hasPose) {
        basePose = `Follow the designated pose reference directive from ${poseSourceDesc} on the TECHNICAL BLUEPRINT board with 100% fidelity. ${
          options.facingCamera
            ? "Ensure the head itself is oriented fully forward to look directly at the camera with confident eye contact."
            : "If the reference pose shows the model turned in profile/3-quarter view, looking away, replicate that exact facial rotation and look away in the same direction."
        }`;
      } else if (options.posture === "original") {
        basePose = "Maintain posture from Image 1.";
      } else if (options.posture === "professional") {
        basePose =
          "POSE (PROFESSIONAL): Position the subject in a classic, confident professional portrait pose with no folded hands. (e.g., slightly angled to the camera, shoulders squared yet relaxed).";
      } else if (options.posture === "casual") {
        basePose =
          "POSE (CASUAL): Position the subject in a natural, relaxed, and approachable casual post.";
      } else {
        basePose = "Maintain posture from Image 1.";
      }

      // 2. Append the profile angle modifications if applicable
      if (options.isProfileView) {
        return `${basePose} Modify the angle so the subject is at a ${options.profileAngle}-degree angle facing ${options.profileDirection}.`;
      }

      return basePose;
    })()}
    - ${lightingDirective}
    - ${photographicExcellence}`;

  // 6. MASTER ETHICS, SAFETY & DECENCY LOCK (Tightened for standalone blouse restriction)
  const safetyEthicsLock = `
  MASTER SAFETY & DECENCY PROTOCOL (STRICT ENFORCEMENT):
  - NO NUDITY, NO UNDRESSING, NO REVEALING ATTIRE: You are forbidden from rendering the subject with any revealing elements.
  - STANDALONE BLOUSE PROHIBITION: Never render a subject wearing only a cultural blouse (saree blouse). If cultural attire is requested or suggested by references, it MUST be a complete professional ensemble including a saree or lehenga that modestly covers the torso.
  - NAVEL COVERAGE (STRICT): For Indian cultural attire (Sarees, Lehengas, etc.), you are strictly forbidden from revealing the subject's navel. Ensure the saree pallu or lehenga dupatta is draped modestly to cover the midriff and navel area completely.
  - CLEAVAGE LOCK: Ensure all attire is high-coverage and modest. If the reference catalogue image shows deep cleavage or is unbuttoned/low-cut, you MUST correct it in the final render to be opaque, high-coverage, and fully professional. Breasts must be completely covered with no visible cleavage.
  - MODESTY OVERRIDE: Prioritize professional modesty over literal reference matching if the reference is revealing. Note: Traditional cultural wear (like Sarees) may involve sheer fabrics; these are professional provided the final render ensures the chest and midriff are modestly covered by a solid underlying blouse.
  - NO SUGGESTIVE POSES: All poses must be neutral, confident, and professional. Arched backs or seductive expressions are strictly forbidden.
  - NO VIOLENCE/WEAPONS: Forbidden elements include blood, weapons, or scary imagery.
  
  STUDIO EQUIPMENT PROHIBITION (ZERO TOLERANCE):
  - You are STRICTLY FORBIDDEN from including any studio equipment, lighting gear, or photography tools in the final output.
  - DO NOT RENDER: softboxes, light stands, strobes, umbrellas, reflectors, backdrop stands, backdrop stand legs, power cables, cameras, or any other gear.
  - EDGE-TO-EDGE CLEANLINESS: Ensure no equipment is visible even at the very edges or corners of the frame. The entire image must be a clean, professional portrait showing ONLY the subject and their environment.
  - ZERO TOLERANCE: Any visible studio gear, especially backdrop stand legs or light strobes, is a direct violation of this protocol.`;

  // 7. Final Prompt Construction
  const prompt = `PRIMARY OBJECTIVE: Create a high-fidelity professional portrait that serves as a pixel-perfect biometric match of the person in Image-1.

  - DESIGNATED TARGET GENDER FOR SUBJECT (Image-1) (CRITICAL & MANDATORY): The subject in Image-1 is strictly a ${
    detectedGender === "male" ? "MALE (Boy / Man)" : "FEMALE (Girl / Woman)"
  }. You MUST ensure the subject is styled and rendered in clothing appropriate to this specific gender, regardless of the gender of any reference model in the blueprints.
  
  - VISUAL BIOMETRIC IDENTITY LOCK: The subject's exact facial identity, bone structure, eyes, nose, mouth, teeth, smile, age, wrinkles, skin tone, complexion, and unique facial markers MUST be referenced directly and strictly from the visual pixels of Image-1. The person rendered must be unmistakably the same individual shown in Image-1 even when transforming or hallucinating to form a profile view or full-length portrait or similar. Strictly keep skin microtextures and pores natural without AI smoothing.
  
  SCENE TRANSFORMATION:
  ${
    targetRef
      ? ` Create a cinematic high-fidelity professional portrait that is a 100% biometric and LITERAL ANATOMICAL match of Image-1, wearing the GENDER-APPROPRIATELY ADAPTED clothing from ${
          dressSnippetIndexes.length > 0
            ? `Snippet ${dressSnippetIndexes.join(", ")}`
            : "the dress/attire reference snippet"
        } (since the subject is strictly a ${
          detectedGender === "male" ? "MALE" : "FEMALE"
        }, you must translate/adapt any opposite-gender outfit into an equivalent ${
          detectedGender === "male" ? "male" : "female"
        } clothing design matching the source's colors, fabric textures, materials, and formality) and transformed into the EXACT posture and pose from ${
          poseSnippetIndexes.length > 0
            ? `${postureInstruction}`
            : "the pose reference snippet as specified in the Replication directives in the TECHNICAL BLUEPRINT."
        }`
      : " wearing the specific attire described in the Transformation Protocol"
  }, with MASTER DECENCY and SOFT STUDIO LIGHTING applied. ${
    options.aspectRatio === "9:16"
      ? "The output MUST be a full-length head-to-toe shot."
      : options.aspectRatio
  }
  
  CRITICAL RULES:
  ${
    hasDress
      ? `- ATTIRE & GARMENT VISUAL REPLICATION: Replicate the garments, fabrics, textures, and accessories directly from ${dressSourceDesc} on the TECHNICAL BLUEPRINT board.`
      : ""
  }
  - FULL SUBJECT REGENERATION (MANDATORY): You must regenerate the entire person (face, neck, torso, limbs, hands) as a 1:1 biometric match of Image-1. Do NOT use the body frame, limbs, hands, or skin of the reference models.
  - No reference catalogue models or subjects should be present in the output image.
  - THE FACE MUST NOT CHANGE. Identity from Image-1 is absolute.
  - NO FACE SWAP: If the replication directive is to replicate pose or replicate dress, there should absolutely be NO FACE SWAPPING. Avoid pasting the subject's face in Image-1 onto the reference model's body, limbs, and skin tone. This is extremely low-quality and causes age, body build, and skin texture mismatches. You must perform a FULL body and face regeneration of the subject in Image-1 that referenced pose if any.
  - GENDER-APPROPRIATE ATTIRE (STRICT & MANDATORY): The subject of Image-1 is strictly a ${
    detectedGender === "male" ? "MALE (Boy / Man)" : "FEMALE (Girl / Woman)"
  }. You are STRICTLY FORBIDDEN from applying ${
    detectedGender === "male"
      ? "female garments (like dresses, skirts, gowns, saree, lehenga, or tight-fitting women's clothing)"
      : "masculine-looking or ill-fitting male garments"
  } to the subject, regardless of any replication directives or references shown in the snippets. If the reference snippet features clothing of the opposite gender, you MUST adapt and translate that outfit into a handsome and appropriate ${
    detectedGender === "male"
      ? "male equivalent (such as a shirt/t-shirt, polo, pants, or styled sweater/blazer/suit jacket)"
      : "female equivalent (such as a professional dress, skirt and blouse, or trousers and styled top/blazer)"
  } matching the exact colors, metal accents, patterns, fabric textures, and formality of the reference.
  ${
    hasDress
      ? `- ATTIRE THEME ADHERENCE: While ensuring gender appropriateness, you MUST maintain the overall aesthetic, fabric texture, color palette, and "dress theme" as defined in ${dressSourceDesc}.`
      : ""
  }
  - ATTIRE INTEGRITY: If no REFERENCE CATALOGUE IMAGE is provided, strictly follow the manual attire description provided in the Transformation Protocol. Do not default to the subject's original casual clothing.
  - POSTURE SEPARATION (STRICT): The posture, body positioning, limb placement, head tilt, and physical gesture must come 100% from ${
    hasPose
      ? `${poseSourceDesc} on the TECHNICAL BLUEPRINT board`
      : "the designated posture directive."
  }
  - SKIN RENDERING & SKIN TONE ANCHOR: Ensure a soft, professional skin texture. Eliminate any oily hotspots or harsh reflections. All visible skin (face, hands, neck, chest, limbs) must 100% match the ethnicity, complexion, and unique age-appropriate texture of the subject in Image-1. Do not use the skin tone, build, or age characteristics of the models in the reference snippets.
  
  ${
    options.textOnlyDirective
      ? `\nATMOSPHERIC SCENE, MOOD, aspectratio, etc. (NON-ATTIRE SUPPLEMENT or minor ATTIRE SUPPLEMENT): ${options.textOnlyDirective}\n`
      : ""
  }
  - HIERARCHY OF TRUTH (STRICT): 
    1. TRANSFORMATION PROTOCOL (Master guide for identity, pose, attire, and grooming. Absolute priority).
    2. ATMOSPHERIC SCENE & MOOD (Supplemental: Use this ONLY for background, setting, and overall photographic atmosphere. It MUST NOT influence or override the attire defined in the Transformation Protocol).
  - THEME-BIAS PREVENTION: You are STRICTLY FORBIDDEN from altering the subject's attire to "match" the background or setting. Even if the background is a specific cultural location, the subject MUST wear the attire defined in the Transformation Protocol. Do not attempt situational "appropriateness" overrides.
  ${safetyEthicsLock}
  ${sceneDirective}
  ${backgroundInstruction}
  ${featuresInstruction}
  ${compositionDirective}

  FINAL MANDATE (ZERO REFERENCE MODEL LEAKAGE):
  - The final generated person MUST BE the EXACT person from Image-1.
  - ZERO LEAKAGE OF REFERENCE MODELS: The identities MUST come 100% and exclusively from Image-1. You are strictly forbidden from allowing the appearance of the models in the reference board to influence or replace the facial features of the subject in Image-1. The reference model is strictly an anonymous mannequin for pose and clothing.
  - Any leakage of the reference model's face, smile, or facial structure into the final output is a failure of the protocol.
  
  Generate exactly one professional studio portrait where the subject from Image-1 is present and transformed.`;

  const parts = [{ text: prompt }];

  parts.push([
    { text: "SUBJECT IDENTITY ANCHOR (Image-1):" },
    { inlineData: { mimeType: mainImage.file.type, data: mainImage.base64 } }
  ]);

  if (maleRef) {
    parts.push({
      text: "TECHNICAL BLUEPRINT (REPLICATE EXACT ATTIRE/JEWELRY/POSTURE ONLY):"
    });
    parts.push({
      inlineData: { mimeType: maleRef.file.type, data: maleRef.base64 }
    });
    parts.push({
      text: "CRITICAL MANDATE: YOU MUST COMPLETELY REPLACE THE PERSONS/MODELS IN THE ABOVE REFERENCE IMAGE WITH THE SUBJECT FROM IMAGE-1. ZERO LEAKAGE ALLOWED."
    });
  }
  if (femaleRef) {
    parts.push({
      text: "TECHNICAL BLUEPRINT (REPLICATE EXACT ATTIRE/JEWELRY/POSTURE ONLY):"
    });
    parts.push({
      inlineData: { mimeType: femaleRef.file.type, data: femaleRef.base64 }
    });
    parts.push({
      text: "CRITICAL MANDATE: YOU MUST COMPLETELY REPLACE THE PERSONS/MODELS IN THE ABOVE REFERENCE IMAGE WITH THE SUBJECT FROM IMAGE-1. ZERO LEAKAGE ALLOWED."
    });
  }

  if (bgRef) {
    parts.push({ text: "ENVIRONMENT LOCATION SOURCE:" });
    parts.push({
      inlineData: { mimeType: bgRef.file.type, data: bgRef.base64 }
    });
  }

  let apiAspectRatio = "3:4";
  if (options.aspectRatio === "1:1") apiAspectRatio = "1:1";
  else if (options.aspectRatio === "4:3") apiAspectRatio = "4:3";
  else if (options.aspectRatio === "16:9") apiAspectRatio = "16:9";
  else if (options.aspectRatio === "9:16") apiAspectRatio = "9:16";

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("GENERATION_TIMEOUT")), 120000)
  );

  try {
    const generationPromise = (async () => {
      let gemini_uri = ENDPOINTS.gemini.index.uri;

      // 1. Send it as an application/json payload instead of FormData
      const res = await fetch(gemini_uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          gemini_action: "generateProfessionalHeadshot",
          parts: parts, // Keeps your array structure completely intact!
          aspectRatio: apiAspectRatio,
          imageSize: is4K ? "4K" : "2K"
        })
      });

      // This handles standard non-200 server failures if they happen
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
      }

      // 1. CRITICAL: Unpack the JSON string stream exactly once
      const data = await res.json();

      // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
      if (data.error) {
        // Standardize the error message into a string variable
        const errMsg =
          typeof data.error === "object" ? data.error.message : data.error;
        // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

        // Track 1: Handle Google's 503 Overloaded Error
        if (
          errMsg.includes("overloaded") ||
          errMsg.includes("demand") ||
          errMsg.includes("503")
        ) {
          alert("MODEL_OVERLOADED! Please retry after sometime.");
          throw new Error("MODEL_OVERLOADED");
        }

        // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
        if (
          errMsg.includes("expired") ||
          errMsg.includes("timeout") ||
          errMsg.includes("504")
        ) {
          alert(
            "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
          );
          throw new Error("SERVER_TIMEOUT");
        }

        // Track 3: General Fallback for other errors
        alert(`Error Encountered: ${errMsg}`);
        throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
      }

      // 3. SUCCESS PATH: Return the clean data object safely to Promise.race

      return data;
    })(); // Invoked immediately as an async IIFE promise

    const response = await Promise.race([generationPromise, timeoutPromise]);

    for (const part of response.candidates[0].content.parts || []) {
      if (part.inlineData) {
        const imageUrl = `data:image/jpeg;base64,${part.inlineData.data}`;
        const usage = response.usageMetadata || {
          promptTokenCount: 0,
          candidatesTokenCount: 0,
          totalTokenCount: 0
        };
        return {
          imageUrl,
          usage: {
            promptTokens: usage.promptTokenCount,
            candidatesTokens: usage.candidatesTokenCount,
            totalTokens: usage.totalTokenCount,
            tokenCost:
              (usage.promptTokenCount / 1000000) * 1.25 +
              (usage.candidatesTokenCount / 1000000) * 5.0,
            imageCost: 0.03,
            estimatedCost:
              (usage.promptTokenCount / 1000000) * 1.25 +
              (usage.candidatesTokenCount / 1000000) * 5.0 +
              0.03
          }
        };
      }
    }
    const noImageReason = extractNoImageReason(response);
    alert(noImageReason);
    // throw new Error(noImageReason);
  } catch (error) {
    if (error.message === "GENERATION_TIMEOUT") {
      throw new Error(
        "The generation process timed out (3 minutes). The Gemini Pro image engine is currently under high load. Please try again in a few moments."
      );
    }
    if (error.message.includes("Requested entity was not found")) {
      throw new Error("API_KEY_RESET_REQUIRED");
    }
    if (
      error.message.includes("503") ||
      error.message.includes("overloaded") ||
      error.message.includes("Service Unavailable")
    ) {
      throw new Error(
        "The image generation service is currently overloaded (503). Please wait 30 seconds and try again."
      );
    }
    throw error;
  }
};

/**
 * Generates a professional group portrait from multiple individual identities.
 */
export const generateGroupPortrait = async (
  apikey,
  is4K,
  members,
  background,
  options,
  styleRef,
  bgRef,
  compositionInstruction,
  snippets
) => {
  // Construct a text block for individual style snippets if they exist to provide direct text guidelines linked to visual sources
  let snippetsInstructionBlock = "";
  if (snippets && snippets.length > 0) {
    snippetsInstructionBlock = `\n  INDIVIDUAL SNIPPETS CONFIGURATION DIRECTIVES (MANDATORY overrides for targeted subjects):
  You MUST apply the following visual details and specific text instructions from each individual snippet drawn on the STYLE REFERENCE image:
  ${snippets
    .map(
      (s, idx) =>
        `  - SOURCE BLUEPRINT: SNIPPET ${
          idx + 1
        }: Replicate style features shown in this image. Specific instructions: "${
          s.instruction || "Replicate exact attire shown."
        }"`
    )
    .join("\n")}\n`;
  }

  // Extract custom blueprint directive from compositionInstruction if present
  let blueprintTextDirective = "";
  let cleanCompositionInstruction = compositionInstruction || "";

  if (
    compositionInstruction &&
    compositionInstruction.includes("CUSTOM DIRECTIVES:")
  ) {
    const parts = compositionInstruction.split("CUSTOM DIRECTIVES:");
    cleanCompositionInstruction = parts[0].trim();
    // Get the part after CUSTOM DIRECTIVES: and remove any trailing text such as "FINAL REQUIREMENT:"
    let rawDirectives = parts[1];
    if (rawDirectives.includes("FINAL REQUIREMENT:")) {
      rawDirectives = rawDirectives.split("FINAL REQUIREMENT:")[0];
    }
    blueprintTextDirective = rawDirectives.trim();
  }

  let backgroundInstruction = "";
  if (bgRef) {
    backgroundInstruction = `ENVIRONMENT & LIGHTING INTEGRATION: 
    - Use the provided BACKGROUND REFERENCE image as the master spatial and lighting guide.
    - COMPOSITION: Frame the shoot so all identity source subjects are naturally positioned WITHIN the environment.
    - LIGHTING: Identity source Subjects must gel perfectly with the environment's lighting (matching direction, color, and intensity).
    - SHADOWS: Apply realistic contact shadows and ambient occlusion where identity source subjects interact with the ground or environment.
    - SCALING: Scale all identity source subjects and any items (like bikes or cars) realistically to fit the background's perspective.`;
  } else if (background.type === "none") {
    backgroundInstruction = `ENVIRONMENT: Strictly maintain the original background from the identity source images. Ensure all subjects are realistically integrated into the existing setting.`;
  } else if (background.type === "reference") {
    backgroundInstruction = `ENVIRONMENT: Use the specific background environment described in the text directive or style reference. Ensure all subjects are realistically integrated into this setting with matching lighting and perspective.`;
  } else {
    backgroundInstruction = `ENVIRONMENT: Place all identity source subjects in a professional studio setting (${background.type}). 
    - Ensure a clean, seamless, and professional studio background.
    - STRICT PROHIBITION: You are FORBIDDEN from including any studio equipment, lighting fixtures, softboxes, strobes, light stands, backdrop stands, or cables in the final image. The background must be a clean, unobstructed studio setting.
    - Ensure consistent studio lighting across the entire group without showing the light sources.`;
  }

  const identityPrompt = members
    .map(
      (m, idx) => `
    SUBJECT IDENTIFIER: "${m.label || "Subject"}" (Image ${idx + 1})
    - IDENTITY ANCHOR: The face, bone structure, and physical identity of "${
      m.label || "Subject"
    }" MUST be a 1:1 biometric match of "IDENTITY SOURCE FOR PERSON ${
        idx + 1
      }". This is the ONLY source for this person's face and identity. You are STRICTLY FORBIDDEN from using the face, features, or identity of the model from the "AESTHETIC & ATTIRE STYLE REFERENCE" for this person.
    - POSTURE REPLICATION (MANDATORY): ${
      m.posture === "original"
        ? "Maintain the EXACT posture and body orientation from Image " +
          (idx + 1)
        : m.posture === "clone"
        ? 'You MUST clone the EXACT physical posture, limb positioning, and body orientation from the corresponding person in the "AESTHETIC & ATTIRE STYLE REFERENCE". This includes the tilt of the head, the position of the hands, and the angle of the torso. Replicate the limb positions with pixel-level spatial accuracy. If the reference shows physical contact (e.g., an arm on a lap), you MUST replicate that contact exactly.'
        : `Adopt a ${m.posture} professional pose.`
    }
    - FULL SUBJECT REGENERATION: You must regenerate the entire person (face, neck, torso, limbs, and hands) as a 1:1 biometric match of Image ${
      idx + 1
    }. Do NOT use the body, hands, or skin of the models in the reference image.
    - HERITAGE & ETHNICITY ANCHOR: Analyze and preserve the specific ethnic heritage of "${
      m.label || "Subject"
    }". unless the replication directive or BLUEPRINT text directive is “Replicate/use this dress/attire” or similar to like use this dress/attire, etc., in which case the dress/attire should be applied as instructed.Do not genericize their features.
    - BIOMETRIC LOCK (ABSOLUTE): Preserve the exact facial details, eyes, nose, mouth, bone structure, and unique facial markers from Image ${
      idx + 1
    } with 100% fidelity.
    - ZERO HALLUCINATION: Do NOT 'improve' or 'beautify' the subject in Image ${
      idx + 1
    }. Maintain their natural age, skin texture, and unique facial markers.
    - EXPRESSION: ${
      m.expression === "original"
        ? "Strictly maintain the ORIGINAL facial expression from Image " +
          (idx + 1) +
          '. This directive ONLY applies to the face and mood. You MUST still apply the attire, jewelry, and overall style from the "AESTHETIC & ATTIRE STYLE REFERENCE" for this subject.'
        : m.expression === "clone"
        ? "Clone the expression and facial mood from the AESTHETIC & ATTIRE STYLE REFERENCE."
        : `Apply a ${
            m.expression === "smile"
              ? "warm studio smile"
              : "confident neutral expression"
          } while strictly preserving the underlying bone structure.`
    }
    - DIRECTIVE ISOLATION (CRITICAL): Any directive to "keep original expression" or "maintain original mood" for "${
      m.label || "Subject"
    }" is strictly limited to the facial muscles and emotional state. It is NOT a directive to keep the original clothing. You MUST still transform the attire of "${
        m.label || "Subject"
      }" to match the "AESTHETIC & ATTIRE STYLE REFERENCE" or the group theme.
    - HAIR LOCK (ABSOLUTE): ${
      m.hairStyle === "keep"
        ? 'Lock the hairstyle, hairline, and volume exactly as seen in "IDENTITY SOURCE FOR PERSON ' +
          (idx + 1) +
          '". Do NOT use the hair from the style reference.'
        : m.hairStyle === "match-ref"
        ? "Adopt the hairstyle/cut from the AESTHETIC & ATTIRE STYLE REFERENCE while maintaining the subject's ORIGINAL hair color."
        : "Maintain original hair length/color but render in a perfectly groomed professional style."
    }
    - BEARD/MUSTACHE: ${
      m.facialHair === "keep"
        ? "Preserve original facial hair features exactly."
        : m.facialHair === "shaven"
        ? "Render the subject with a perfectly clean-shaven appearance."
        : m.facialHair === "clone"
        ? "Clone the facial hair style from the AESTHETIC & ATTIRE STYLE REFERENCE."
        : "Maintain original professional grooming standards."
    }
    - EYEWEAR: ${
      m.eyewear === "keep"
        ? "Strictly preserve the original eyewear from Image " + (idx + 1)
        : m.eyewear === "clone"
        ? "Clone the eyewear style from the AESTHETIC & ATTIRE STYLE REFERENCE."
        : m.eyewear === "remove"
        ? "Remove all eyewear; the subject must be rendered without glasses."
        : "Maintain original professional grooming standards."
    }
    - PHYSIQUE LOCK (ABSOLUTE): Maintain the EXACT body build, weight, and frame of "${
      m.label || "Subject"
    }". Do not alter their proportions.
    - GENDER-APPROPRIATE ATTIRE (STRICT): You MUST analyze the gender of "${
      m.label || "Subject"
    }". You are STRICTLY FORBIDDEN from applying female-specific attire (like dresses or skirts) to a male subject, or male-specific attire to a female subject, regardless of the mapping. If "${
        m.label || "Subject"
      }" is a boy mapped to a girl in a dress, you MUST render him in a professional boy's outfit (e.g., a shirt, sweater, or blazer) that matches the group's color theme.
    - ATTIRE THEME ADHERENCE: While ensuring gender appropriateness, you MUST maintain the overall aesthetic, color palette, and "dress theme" of the group as defined in the "AESTHETIC & ATTIRE STYLE REFERENCE".
    - SKIN TONE ANCHOR: All visible skin (face, hands, neck) must 100% match the ethnicity and complexion of Image ${
      idx + 1
    }.
    - ANATOMICAL ISOLATION (IDENTITY ONLY): You are FORBIDDEN from using any body parts (hands, fingers, neck, legs, skin) or the body build from the style reference for "${
      m.label || "Subject"
    }". However, you MUST follow the SPATIAL POSITIONS of these limbs as defined in the reference.
    - IDENTITY THEFT PREVENTION: You are FORBIDDEN from using any facial features, hair, body characteristics, or skin characteristics from the "AESTHETIC & ATTIRE STYLE REFERENCE" for "${
      m.label || "Subject"
    }".
    - Clothing: Professional attire matching the overall group theme and instructions.
      * GLOBAL BLUEPRINT CLOTHING MANDATE: You MUST strictly respect and prioritize the global "BLUEPRINT TEXT DIRECTIVE" ("${
        blueprintTextDirective || "None"
      }") and "DETECTED ARRANGEMENT & MAPPING" instructions to define the precise style, color, materials, items of clothing, or any accessories for "${
        m.label || "Subject"
      }".
      * CLOTHING REPLICATION INTERPRETATION: If the instructions or blueprint directives mention "replicate exact dress", "replicate exact dress style", "exact dress", or "replicate this dress/attire/clothing", you MUST interpret "dress" as a collective "dress code" or "group attire ensemble" from the style reference image. Replicate the exact outfit worn by the corresponding person in the "AESTHETIC & ATTIRE STYLE REFERENCE" image (adapted for gender if necessary: male subjects MUST wear the exact jacket/shirt/pants style and color seen on the corresponding male model, or a male-equivalent translation of matching color/formality if mapped to a female).
      * REFERENCE ATTIRE CLONE: If this subject is mapped to a person in the reference, we must 100% clone the specific clothing (style, color, cut, fabric texture) of that corresponding person in the "AESTHETIC & ATTIRE STYLE REFERENCE" with extreme detail (unless gender-appropriate adaptation is required).
      * EXTRA PERSON CLOTHING: If this subject is an EXTRA person (not in the reference), provide a professional outfit that matches the group's color scheme and formality perfectly.
  `
    )
    .join("\n");

  let apiAspectRatio = "4:3";
  if (options.aspectRatio === "1:1") apiAspectRatio = "1:1";
  else if (options.aspectRatio === "3:4") apiAspectRatio = "3:4";
  else if (options.aspectRatio === "16:9") apiAspectRatio = "16:9";
  else if (options.aspectRatio === "9:16") apiAspectRatio = "9:16";

  let framingInstruction = "";
  if (options.aspectRatio === "9:16") {
    framingInstruction = `MANDATORY FULL-LENGTH PORTRAIT: You MUST render all identity source subjects from HEAD-TO-TOE including feet. Even if the identity source images are only half-length or headshots, you MUST accurately hallucinate and generate the lower bodies, legs, and shoes for all identity source subjects in a professional manner that matches their attire. The entire human silhouettes MUST be visible from top to bottom within the 9:16 frame.`;
  }

  const lightingDirective =
    options.lightingMode === "even"
      ? `LIGHTING PROTOCOL (EVEN & DIFFUSED): 
       - Apply soft, wrap-around lighting that illuminates the entire group evenly.
       - Ensure all faces are bright, clear, and well-illuminated with a soft, professional finish.
       - STRICTLY FORBIDDEN: Harsh specular highlights or hard-edged shadows on any subject.
       - NO VISIBLE EQUIPMENT (ABSOLUTE): You are STRICTLY FORBIDDEN from rendering any light sources, softboxes, strobes, reflectors, backdrop stand legs, or stands. Only the soft lighting effect on the subjects should be present. Any visible gear is a direct violation.`
      : `LIGHTING PROTOCOL (DRAMATIC & CINEMATIC): 
       - Apply high-contrast lighting with a strong key light effect and deep, moody shadows for the entire group.
       - Incorporate subtle rim lighting to separate all subjects from the background.
       - Emphasize facial contours and textures for a cinematic, high-end editorial look across the group.
       - NO VISIBLE EQUIPMENT (ABSOLUTE): You are STRICTLY FORBIDDEN from rendering any strobes, reflectors, softboxes, backdrop stand legs, or lighting fixtures. Only the cinematic lighting effect on the subjects should be present. Any visible gear is a direct violation.`;

  const prompt = `PRIMARY OBJECTIVE: Create a high-fidelity PROFESSIONAL GROUP PORTRAIT containing EXACTLY ${
    members.length
  } people.

  CRITICAL: IDENTITY REPLACEMENT & LEAKAGE PREVENTION (ZERO TOLERANCE):
  - You are STRICTLY FORBIDDEN from retaining ANY facial features, hair, skin textures, or physical identities from the "AESTHETIC & ATTIRE STYLE REFERENCE".
  - The people in the AESTHETIC & ATTIRE STYLE REFERENCE" image are 100% PLACEHOLDERS.
  - GENDER-APPROPRIATE ATTIRE (STRICT): You MUST analyze the gender of each source subject. You are STRICTLY FORBIDDEN from applying female-specific attire (like dresses or skirts, or saree or blouse or lehanga or etc.) to a male subject, or male-specific attire to a female subject, regardless of the mapping. If the source subject is a boy mapped to a girl in a dress, you MUST render him in a professional boy's outfit (e.g., a shirt, sweater, or blazer or similar to reference dress) that matches the group's color and dress theme.
  - ATTIRE THEME ADHERENCE: While ensuring gender appropriateness, you MUST maintain the overall aesthetic, color palette, and "dress theme" of the group as defined in the "AESTHETIC & ATTIRE STYLE REFERENCE".
  - Any leakage of the "AESTHETIC & ATTIRE STYLE REFERENCE" models' faces, ethnicity, or specific physical traits into the final output is a CRITICAL FAILURE.
  - Every single person in the final image MUST be a perfect biometric match of the ${
    members.length
  } identity source subjects provided.

  CONTEXTUAL ATTIRE CLARITY (MANDATORY):
  - "THIS DRESS/ATTIRE" in MASTER COMPOSITION BLUEPRINT: Any instruction within the "MASTER COMPOSITION BLUEPRINT" or "DETECTED ARRANGEMENT & MAPPING" that mentions "this dress", "this attire", "this outfit", or "these clothes" refers EXCLUSIVELY to the clothing visible in the group reference image above the text 'MASTER BLUEPRINT COMPOSITION" in the AESTHETIC & ATTIRE STYLE REFERENCE" image.
  - "THIS DRESS/ATTIRE" in INDIVIDUAL SNIPPETS: Any instruction within an individual snippet that mentions "this dress", "this attire", "this outfit", or "these clothes" refers EXCLUSIVELY to the clothing visible in that specific snippet's source image.
  - NO CROSS-POLLINATION (ABSOLUTE): You are STRICTLY FORBIDDEN from applying an attire style from an individual snippet to any subject who is NOT explicitly mapped to that snippet or mentioned in the snippet for dress replication. For example, if a subject has a "sub-4" identifier and has a snippet with a unique dress with replication directive as "Replicate/Use this dress for sub-4", ONLY "sub-4" identifier should wear that dress. Other identifiers like "sub-1", "sub-2", and "sub-3" MUST NOT wear the dress from "sub-4's" snippet.
  
  CONTEXTUAL POSE CLARITY (MANDATORY):
  - "THIS POSE/POSTURE" in MASTER COMPOSITION BLUEPRINT: Any instruction within the "MASTER COMPOSITION BLUEPRINT" or "DETECTED ARRANGEMENT & MAPPING" that mentions "this pose" or "this posture" refers EXCLUSIVELY to the pose visible in the "MASTER COMPOSITION BLUEPRINT" image only.
  - "THIS POSE/POSTURE " in INDIVIDUAL SNIPPETS: Any instruction within an individual snippet that mentions "this pose" or "this posture" refers EXCLUSIVELY to the pose visible in that specific snippet's image only.
  - POSE/POSTURE Application: Any instruction in the "MASTER COMPOSITION BLUEPRINT" or REPLICATION DIRECTIVE in snippets similar to “Replicate this pose/posture” or “Use this pose/posture” without any identity source subject identifiers means, the pose has to be applied to all “IDENTITY SOURCE” images.
  - NO CROSS-POLLINATION (ABSOLUTE): You are STRICTLY FORBIDDEN from applying a pose from an individual snippet to any identity source subject who is NOT explicitly mapped to that snippet or mentioned in the snippet for pose replication. For example, if a identity source subject has a "sub-4" identifier and has a snippet with a unique pose with replication directive as replicate/use this pose for sub-4, ONLY "sub-4" identifier should be applied that pose. Other subjects with identifiers like "sub-1", "sub-2", and "sub-3" MUST NOT be applied that pose.


  - ATTIRE REPLICATION & EXTRA ELEMENT ISOLATION (MANDATORY):
    - Any directive to add pets, animals, props, or extra figures (not in the reference image) MUST NOT interfere with the attire replication of the primary subjects.
    - If the "MASTER COMPOSITION BLUEPRINT" says "Replicate this exact dress" or "Use this attire", you MUST apply that attire to all subjects mapped to the reference image, regardless of the presence of a pet or extra figure.
    - GENDER-SENSITIVE REPLICATION (ABSOLUTE): If a subject is mapped to a figure of a different gender in the reference image, "Replicate this exact dress" MUST be interpreted as "Replicate the color, material, texture, and style of this dress in a GENDER-APPROPRIATE version". You are STRICTLY FORBIDDEN from rendering a male subject in a dress or a female subject in male-specific dress. For example, if a boy is mapped to a girl in a white textured dress, render the boy in a white textured shirt or sweater of the same material, etc.
    - The addition of a pet (e.g., "Add dog to the right") is a spatial directive only. It is NOT a directive to revert the subjects' clothing to their original state. You MUST maintain the requested attire replication for the human subjects while simply adding the pet to the scene.
    - ZERO FALLBACK: Adding a pet is NOT an excuse to use the subjects' original clothing from their source images. You MUST still transform their attire to match the "AESTHETIC & ATTIRE STYLE REFERENCE" or the group theme.

  
  INDIVIDUAL SNIPPET SUPREMACY & DIRECTIVE HIERARCHY (ABSOLUTE):
  - Any specific instruction within the "MASTER COMPOSITION BLUEPRINT" or "DETECTED ARRANGEMENT & MAPPING" that targets a specific subject identifier (e.g., "${members
    .map((m) => m.label)
    .join('", "')}") MUST be treated as an absolute override for that subject.
  - HIERARCHY OF TRUTH (STRICT): 
    1. INDIVIDUAL SNIPPET DIRECTIVE (Absolute Master: Overrides everything for the targeted subject).
    2. GLOBAL STYLE REFERENCE (Master guide for the group formation and poses).
    3. SCENE THEME DIRECTIVE / Text Directive (Supplemental: Use ONLY for background, lighting, and general scene setting. It MUST NOT override attire or identity instructions of snippets or references).
    4. DEFAULT OPTIONS (Fallback).
  - THEME-MATCHING PROHIBITION: You are STRICTLY FORBIDDEN from changing a subject's attire to match the "vibe" or "location" of the SCENE THEME DIRECTIVE. If a snippet says "Blue Suit" and the scene is "Taj Mahal", you MUST render a Blue Suit. Do not "hallucinate" traditional wear for the sake of theme matching.
  - OVERRIDE SCOPE: If a snippet says "Use this dress for ${
    members[0].label || "Subject"
  }", you MUST apply that dress to that subject even if the "AESTHETIC & ATTIRE STYLE REFERENCE" or the global theme mentions something different. This applies to attire, jewelry, pose, arrangement, and props.
  - POSTURE PRESERVATION (CRITICAL): Applying a specific attire or jewelry directive from a snippet MUST NOT alter the subject's negotiated posture or body orientation. You MUST maintain the negotiated posture (either from the reference or the source) while only changing the requested attribute.
  - NO GLOBAL REFERENCE FALLBACK: If no "AESTHETIC & ATTIRE STYLE REFERENCE" is provided, the "MASTER COMPOSITION BLUEPRINT" and individual snippets are your ONLY source of truth for attire and poses. You MUST follow them promptly and accurately.

  ATTRIBUTE ISOLATION & INDIVIDUAL SETTING SUPREMACY (ZERO TOLERANCE):
  - Any directive to "replicate pose", "use this pose", or "follow the composition" is STRICTLY LIMITED to the anatomical positioning of limbs, torso, and head.
  - HAIRSTYLE ISOLATION: You are STRICTLY FORBIDDEN from allowing a "pose" directive to influence a subject's hairstyle. If a subject's individual setting is "Lock the hairstyle" (HAIR LOCK), you MUST honor that regardless of the reference image's hairstyle.
  - EXPRESSION ISOLATION: You are STRICTLY FORBIDDEN from allowing a "pose" directive to influence a subject's facial expression. If a subject's individual setting is "Maintain ORIGINAL facial expression", you MUST honor that.
  - INDIVIDUAL SETTING PRIORITY: The individual settings for each subject (HAIR LOCK, EXPRESSION, POSTURE) MUST always take precedence over any global or composition-level directive. 
  - If a subject's POSTURE is set to "Maintain the EXACT posture", you MUST ignore the reference image's pose for that specific subject and keep their original body orientation, even if the rest of the group follows the reference.

  POSE MASTER PROTOCOL (ZERO DEVIATION):
  - The "AESTHETIC & ATTIRE STYLE REFERENCE" is your absolute POSE MASTER.
  - If there is a MASTER COMPOSITION BLUEPRINT and a group reference image, You MUST replicate the EXACT physical pose of every single person from the group reference image if the replication directive for that BLUEPRINT DIRECTIVE is “Replicate this pose” or “Use this pose” or similar.
  - If there is a snippet and an associated snippet image, You MUST replicate the EXACT physical pose of the person in that specific snippet reference image if the replication directive for that snippet is “Replicate this pose” or “Use this pose” to the subjects as identified by the identifiers. In the absence of any subject identifiers, it applies to all “IDENTITY SOURCE” images. Remember, individual snippet pose overrides, MASTER COMPOSITION BLUEPRINT” pose if any.
  - POSE DEFINITION: A 'pose' is defined EXCLUSIVELY as the anatomical arrangement of the body, limbs, and head. It does NOT include hairstyle, facial expression, or clothing. You are STRICTLY FORBIDDEN from using the hairstyle of the reference model when replicating their pose.
  - This includes: precise limb angles, leg crossing patterns, hand placements (including contact with other subjects), head tilts, and torso rotations.
  - If a person in the reference has their legs crossed, the subject in your output MUST have their legs crossed in the exact same manner.
  - If a hand is resting on a shoulder, it MUST be resting on that shoulder in the output.
  - If a person in the reference image is facing to their right, the subject in your output MUST also should be facing to their right.
  - "Similar to" is a failure. "Exact replication" is the only acceptable outcome.
  - The poses must be followed with pixel-level skeletal spatial accuracy relative to the group formation.
  - POSE OVERRIDE (CONDITIONAL): The pose from the reference image MUST override the subject's individual source image pose ONLY IF the subject's individual posture setting is NOT "Maintain the EXACT posture". If "Maintain the EXACT posture" is selected, the source pose MUST be preserved.
  - NO LEKAGE (CRITICAL): The person/figure/model/subject present in MASTER COMPOSITION BLUEPRINT or individual snippet images should absolutely NOT BE PRESENT, eith in whole or in body parts like face, hand and or fingers, etc. in the final output. Present of reference subject is considered an "ABSOLUTE FAILURE".

  WHAT WE CAN IMPROVE FOR EVEN GREATER POSE ACCURACY (MANDATORY):
  1. MICRO-ANGLES & SKELETAL SKELETON: Consider the entire group as a jointed skeletal blueprint. You MUST lock and replicate:
     * Precise head tilts (e.g. slight incline of a daughter's head toward her father, or mom tilting her neck toward her husband).
     * Exact torso tilt vectors (e.g. leaning slightly inward, or hips angled at a 45-degree rotation relative to the camera lens).
     * Exact 3D elbow, shoulder, and knee joint articulation angles.
  2. INTER-PERSON PHYSICAL CONTACT POINTS & TOUCH FIDELITY:
     * Replicate ALL hand-to-body contacts. If a sister places her hand on her brother's collarbone/shoulder (as in Image 3), or if a father wraps his fingers around his daughter's shoulder (as in Image 1), you MUST render this physical connection exactly with visible fingernails, hand placement, and correct overlap.
     * Interlocking arms, hands held on laps, or heads leaning on shoulders must be simulated with absolute spatial and contact realism. Hands must never "float" or slide into pockets unless depicted that way in the reference.
  3. RELATIVE EYE-LEVELS, HEIGHT PLATFORMS & SPATIAL STAGGERING:
     * Strictly preserve relative height alignment. If the reference holds 4 eye-levels (Dad tallest in back row center-left, daughter center-right, Mom sitting lowest, boy sitting intermediate as in Image 1), these precise vertical proportions MUST be duplicated.
     * Scale the heads and bodies to maintain these spatial intervals.
     * If subjects sit on supports (e.g. stools, modern cubic boxes, blocks), recreate that furniture item in corresponding position and geometry. Ensure the subjects' knees are bent, and thighs are supported exactly at the height of the platform with feet resting on the floor or dangling precisely where the reference shoes rest.
  4. GEOMETRIC DENSITY, PROXIMITY & NEGATIVE SPACE:
     * Replicate the exact group density (cuddle-closeness vs formal separation). Do NOT spread subjects apart if they are huddled close in the reference.
     * Maintain the exact negative space gaps between ears, shoulders, and hips of adjacent subjects.
  5. FABRIC DRAPE & SURFACE COLLISIONS:
     * When pants or skirts collide with seat tops/stools, match the silhouette and physical drape folds from the reference. The clothing must physically fold, stretch, or bunch up over legs, thighs, or furniture surfaces, serving as a functional pose anchor.

  MANDATORY SUBJECT COUNT & PRESENCE (STRICT):
  - You MUST include all ${
    members.length
  } distinct individuals provided in the "IDENTITY SOURCE" images.
  - EXACT COUNT: The final image MUST contain exactly ${
    members.length
  } people. No more, no less.
  - NO DUPLICATION (STRICT): Each of the ${
    members.length
  } identity source subjects MUST appear EXACTLY ONCE in the final image. You are STRICTLY FORBIDDEN from duplicating any single source identity or using the same face for multiple figures in the group. Even if the mapping is ambiguous, you must ensure that every person in the output is a unique individual from the source set.
  - NO OMISSIONS: Every single person from the ${
    members.length
  } source images must be clearly visible and identifiable in the final group portrait.
  - REFERENCE FIGURE ELIMINATION (ABSOLUTE): You are STRICTLY FORBIDDEN from retaining ANY figure, person, or silhouette from the "AESTHETIC & ATTIRE STYLE REFERENCE". 
  - Every person in the final output MUST be one of the ${
    members.length
  } subjects from the "IDENTITY SOURCE" images.
  - If a person in the "AESTHETIC & ATTIRE STYLE REFERENCE" image is not explicitly mapped to a source subject, or if multiple source subjects are mapped to the same position, you MUST NOT keep the original person from the reference image in that position. 
  - The "AESTHETIC & ATTIRE STYLE REFERENCE"  image is a POSE and COMPOSITION guide ONLY. It is NOT a source of people. 
  - NO REFERENCE MODELS: You are STRICTLY FORBIDDEN from including any of the models or people from the "AESTHETIC & ATTIRE STYLE REFERENCE" in the final image. They are placeholders ONLY. 
  - MAPPING IS SUBJECT IDENTIFIER or LABEL DRIVEN (CRITICAL): You MUST map subjects based EXCLUSIVELY on the labels and subject identifiers provided in the "DETECTED ARRANGEMENT & MAPPING" instructions. You are STRICTLY FORBIDDEN from mapping based on gender, age, or ethnicity.
  - SNIPPET DIRECTIVE ADHERENCE: You MUST scan the "MASTER COMPOSITION BLUEPRINT" and individual snippet REPLICATION DIRECTIVES for any text directives associated with these subject identifiers. If a subject identifier has a specific attire, pose, or accessory instruction, apply it with 100% fidelity as a master override.
  - GENDER-NEUTRAL MAPPING (STRICT): If a male subject is mapped to a female figure in the reference (or vice versa), you MUST place the source subject in that position. You are FORBIDDEN from keeping the reference model just because the genders do not match. 
  - AGE-NEUTRAL MAPPING (STRICT): If a older subject is mapped to a youger figure in the reference (or vice versa), you MUST place the source subject in that position. You are FORBIDDEN from keeping the reference model's age just because the age does not match. You should always retain source subject's age. 
  - ATTIRE ADAPTATION: In cases of gender mismatch during mapping, you must synthesize a professional, gender-appropriate outfit for the source subject that matches the color, material, and formality of the group's theme. For example, if a man is mapped to a woman in a brown dress, render the man in a brown professional suit or blazer.
  - SNIPPET OVERRIDE: If a snippet explicitly requests a specific outfit (e.g., "Use this dress for 'sub-1'"), you MUST honor that request even if it contradicts the gender-appropriate fallback or the global reference. The snippet is the ultimate authority.
  - EXTRA SUBJECT HANDLING: In the MASTER COMPOSITION BLUEPRINT, ff there are more source images (${
    members.length
  }) than number of people in the MASTER COMPOSITION BLUEPRINT image, you MUST add the extra identity source subjects to the group in a cohesive, natural way. Place them in positions as instructed in the directive. If no instructions given place them in logical positions (e.g., standing behind or sitting beside the main group) and ensure their attire and lighting match the overall theme perfectly.
  - NO MERGING: Do not merge two people into one or replace one person with another.
  
  STUDIO EQUIPMENT PROHIBITION (ZERO TOLERANCE):
  - You are STRICTLY FORBIDDEN from including any studio equipment, lighting gear, photography tools, or artifacts in the final output.
  - DO NOT RENDER: softboxes, light stands, strobes, umbrellas, reflectors, backdrop stands, backdrop stand legs, power cables, cameras, or any other gear.
  - STUDIO ENVIRONMENT VS. EQUIPMENT (CRITICAL): While the lighting, quality, and setting should reflect a "high-end studio shoot" with "dramatic and cinematic lighting," the actual TOOLS used to achieve this must be 100% INVISIBLE. 
  - The final image must be a clean, professional portrait showing ONLY the subjects and their immediate environment (e.g., a clean backdrop, professional flooring, or a simple stool/chair).
  - EDGE-TO-EDGE CLEANLINESS: Ensure no equipment is visible even at the very edges or corners of the frame. The entire image must be a clean, professional portrait showing ONLY the subjects and their environment.
  - ZERO TOLERANCE: Any visible studio gear, especially light modifiers (like softboxes) or backdrop hardware, is a direct violation of this protocol.
  
  CRITICAL IDENTITY & HERITAGE PRESERVATION (ZERO TOLERANCE):
  Each person in the final image MUST be a perfect biometric recreation of the individual identity source images provided. 
  - NO GENERIC SUBJECTS: Treat each person as a unique individual with distinct ethnic features and heritage.
  - NO ANATOMICAL OR BUILD LEAKAGE (ABSOLUTE): Do not use hands, limbs, skin tones, or body builds of any of the models or people from the "AESTHETIC & ATTIRE STYLE REFERENCE" in the final image. The physical frame of each subject MUST come 100% from their respective identity source image.
  - PHYSIQUE INTEGRITY: Every identity source subject must retain their original weight and body frame.
  - ETHNICITY LOCK: Ensure absolute skin tone and heritage consistency for each identity source subject.
  ${identityPrompt}

  REFERENCE ISOLATION & IDENTITY MANDATE (ZERO-TOLERANCE):
  - SUBJECT IDENTIFIERS: The labels (${members
    .map((m) => `"${m.label || "Subject"}"`)
    .join(", ")}) are the absolute master identifiers for the subjects.
  - MASTER COMPOSITION BLUEPRINT: The "AESTHETIC & ATTIRE STYLE REFERENCE" is your absolute master guide for the group's spatial arrangement, depth, and individual body poses. You MUST replicate the group formation and the specific pose of each individual from this reference image with pixel-level spatial accuracy. 
  ${
    cleanCompositionInstruction
      ? `- DETECTED ARRANGEMENT & MAPPING: Use the following detected arrangement and user-defined mapping identifiers: ${cleanCompositionInstruction}`
      : ""
  }
  - POSE SUPREMACY (MANDATORY): Replicating the exact physical pose of each individual from the MASTER COMPOSITION BLUEPRINT” IF PRESENT is your TOP PRIORITY. The limb positions, hand placements, and body angles must be replicated exactly. If the reference shows crossed legs, the output MUST show crossed legs. If a hand is resting on a lap, it MUST be resting on that lap. "Similar to" is a failure; "Exact replication" is the only acceptable outcome.
    * SEATED LEG CROSSING MANDATE (CRITICAL): If the reference image shows a seated person crossing one leg over the other (e.g. the mother figure seated on the stool on the left crossing her right leg over her left thigh), you MUST duplicate this exact leg-crossing pattern. Rendering them with uncrossed legs or simple parallel legs is a direct violation of this pose protocol.
    * SKELETAL MICRO-ANGLES & LEVER VECTOR: Mirror the exact degree of head tilts, shoulder slouch, shoulder rotation, waist lean, and joint flexing angles of every model.
    * TOUCH LOCK & LIMB INTERACTION: Replicate precise touch alignments (e.g., fingers placed flat on another's clothes or shoulders). Touch elements must not float or drift.
    * SPATIAL EYE-LEVEL STAGGERING: Match the exact relative eyeball and nose-bridge vertical heights and furniture seating heights in the composition layout.
    * Mirror all physical interactions, contact points, hand rests, and overlapping limbs. "Similar to" is a failure; "Exact replication" is the only acceptable outcome.
  - MANDATORY IDENTITY PLACEMENT (NO EXCEPTIONS): DO NOT SUBSTITUTE or USE any of the REFERENCE MODELS as identity source subjects just because they are already in that pose. You MUST place the identity of each subject from the "IDENTITY SOURCE" images into their mapped position in the group in "MASTER COMPOSITION BLUEPRINT” IF PRESENT in that exact pose. You are STRICTLY FORBIDDEN from using the face, hair, or physical identity of a model from the "AESTHETIC & ATTIRE STYLE REFERENCE" under any circumstances, including cases where the age, gender, or ethnicity of the source subject does not match the model in the reference image.
  - SUBJECT MAPPING & IDENTITY FALLBACK: Map the subjects from the "IDENTITY SOURCE" (sub-1, sub-2, etc.) to the subjects in the “MASTER COMPOSITION BLUEPRINT” image if present using the "DETECTED ARRANGEMENT & MAPPING" identifiers provided in the composition instruction. If the instruction says "Map Figure 1 to John", where John maybe the subject identifier, you MUST place the identity source subject with the subject identifier or label "John" in the position described for "Figure 1". If no explicit mapping is provided for an identity source subject, map them logically (e.g., sub-1 maps to Figure 1, sub-2 maps to Figure 2, and so on). 
  - CRITICAL IDENTITY FALLBACK: If you cannot determine a clear 1:1 mapping for a subject in the reference image, or if you have extra subjects, you MUST still use the provided "IDENTITY SOURCE" images. You are STRICTLY FORBIDDEN from defaulting to the face, hair, or identity of a model from the reference image.
  - SUBJECT IDENTIFIER or LABEL-DRIVEN MAPPING (MANDATORY): You MUST analyze the gender and age of each identity source subject in the "IDENTITY SOURCE" images. You MUST map identity source subjects to the reference positions based EXCLUSIVELY on the subject identifiers or labels provided in the "DETECTED ARRANGEMENT & MAPPING" instructions or in the replication blueprint directives. 
  - GENDER-NEUTRAL PLACEMENT: You are STRICTLY FORBIDDEN from using gender as a mapping criterion. If a male subject is mapped to a female figure in the reference, you MUST place the male subject in that position.
  - AGE-NEUTRAL MAPPING: If a older subject is mapped to a youger figure in the reference (or vice versa), you MUST place the older subject in the younger figure's position. You are FORBIDDEN from keeping the reference model's age just because the age does not match. You should always retain source subject's age. 
  - GENDER-APPROPRIATE ATTIRE ENFORCEMENT (ZERO TOLERANCE): You MUST NOT apply gender-inappropriate clothing. A male subject MUST NEVER be rendered in a dress or skirt. A female subject MUST NEVER be rendered in male-specific undergarments or ill-fitting male attire. You MUST synthesize a professional, gender-appropriate outfit for the source subject that matches the color, material, and formality of the group's theme.
  - ATTIRE FALLBACK & EXTRA SUBJECTS: DO NOT SUBSTITUTE or USE any of the REFERENCE MODELS as identity source subjects just because they are already weaaring that attire/dress. If the mapping is ambiguous, if there's a gender/age mismatch, or if the subject is an EXTRA person not in the reference, you MUST ignore the reference attire for that person. Instead, render them in a professional, gender-appropriate outfit (e.g., a suit for a boy, a dress/blazer for a girl) that matches the overall group's color palette, dress theme and formality. THE IDENTITY MUST STILL BE OF THE IDENTITY SOURCE SUBJECT.
  - DISCARD REFERENCE MODELS ENTIRELY: STRICTLY DO NOT SUBSTITUTE or USE any of the REFERENCE MODELS as identity source subjects for pose or attrire/dress replication just because the reference models are already in that pose or wearing that dress for replication respectively. You MUST completely ignore and discard the faces, hair, skin, expressions, and physical bodies (weight, frame, height) of the models in the style reference image. The people in the reference image are placeholders; you must replace them with the subjects from the IDENTITY SOURCE images. Even if a source subject is a child being mapped to an adult model in the reference, you MUST render the child's identity in that position, adjusting the scale as necessary.
  - IDENTITY REPLACEMENT MANDATE: You are strictly forbidden from allowing the reference models to "influence" the output faces. You must perform a total identity replacement.
  - ZERO TOLERANCE FOR REFERENCE MODELS: If the final image contains any person, face, or silhouette that is NOT one of the ${
    members.length
  } identity source subjects, it is a direct violation of this protocol. You must ensure that every single figure from the "AESTHETIC & ATTIRE STYLE REFERENCE" image is 100% removed and not present in the final output image.
  - UNMAPPED FIGURE REMOVAL: If a figure in the reference image is not mapped to any of the ${
    members.length
  } identity source subjects, or if it is explicitly listed as REMOVED in the instructions, that figure MUST be completely removed from the scene. You are STRICTLY FORBIDDEN from leaving a reference model or adding a generic person in the final output. The final image MUST contain EXACTLY ${
    members.length
  } people.
  - IDENTITY ANCHOR & LEAKAGE PREVENTION: The identities MUST come 100% and exclusively from the "IDENTITY SOURCE" images. You are strictly forbidden from allowing the appearance of the models in the style reference to influence the facial features of the subjects. Any leakage of the reference models' faces into the final output is a failure of the protocol.
  - ZERO DEVIATION: Do not modify the subject's facial identity even minutely. The output must be a biometric match for ALL ${
    members.length
  } people.
  - FINAL VERIFICATION: Before generating the final pixels, you MUST verify that every single figure in the scene is of the ${
    members.length
  } identity source subjects and that NO SUBJECT IS DUPLICATED OR REPLACED BY A REFERENCE MODEL. If a position in the MASTER COMPOSITION BLUEPRINT image is not occupied by a unique IDENTITY source subject, that position MUST be left empty or the figure MUST be removed. Under no circumstances should a figure from the reference image remain in the final output, nor should any source subject appear more than once.
  - ATTIRE ISOLATION VERIFICATION: Ensure that any attire from an individual snippet is applied ONLY to the specific identity source subject it was intended for. Subjects mentioned in the "COMPOSITION BLUEPRINT" who do NOT have an individual snippet MUST get their attire from the MASTER COMPOSITION BLUEPRINT in the "AESTHETIC & ATTIRE STYLE REFERENCE".

  ${
    blueprintTextDirective
      ? `BLUEPRINT TEXT DIRECTIVE (MANDATORY GLOBAL BASELINE):
  - You MUST strictly apply these supreme baseline instructions for overall clothing styles, background modifications, pet additions, or group composition layout settings unless overridden by individual snippet instructions:
  "${blueprintTextDirective}"`
      : ""
  }

  DETAILED COMPOSITION BLUEPRINT:
  ${cleanCompositionInstruction}

  FRAME-AGNOSTIC CONTENT REPLICATION (MANDATORY):
  - You MUST decouple the content (poses, attire, identities, and group formation) from the frame's aspect ratio.
  - Regardless of whether the "AESTHETIC & ATTIRE STYLE REFERENCE" is portrait (9:16, 3:4) and the output is landscape (16:9, 4:3), or vice versa, you MUST preserve the EXACT posture, limb positions, and attire styles from the reference for every mapped subject.
  - If the output aspect ratio is wider than the reference, expand the background environment horizontally while keeping the group's central formation and individual poses identical to the reference.
  - If the output aspect ratio is taller than the reference, expand the background environment vertically (e.g., showing more floor or ceiling) while maintaining the group's integrity.
  - DO NOT change the poses or attire just because the aspect ratio has changed. The subjects must remain in their requested positions and clothing regardless of the frame shape.

  COMPOSITION & PHOTOGRAPHIC QUALITY:
  - ${framingInstruction}
  - SPATIAL REPLICATION: You MUST replicate the group formation, depth, and individual body poses from the "AESTHETIC & ATTIRE STYLE REFERENCE" with absolute fidelity. The spatial arrangement of subjects in the final image must be a 1:1 match of the reference image's composition.
  - PHYSICAL INTERACTION & CONTACT (CRITICAL): Replicate all physical interactions and contact points between subjects seen in the MASTER COMPOSITION BLUEPRINT if present in the "AESTHETIC & ATTIRE STYLE REFERENCE". This includes hands resting on shoulders, arms resting on another person's lap, subjects leaning against each other, or any form of physical touch. These interactions are vital for a natural and accurate group portrait.
  - GROUP COHESION: The identity source subjects must appear as a unified group, with natural physical interactions and overlapping limbs as seen in the reference. Ensure that when one subject's limb (like an arm) is placed on another subject (like a lap), the contact looks realistic and anatomically correct.
  - GROUP Subject Expression (CRITICAL). The facial expressions of the subjects MUST be determined primarily by their individual SUBJECT IDENTIFIER "EXPRESSION" rules (e.g., maintaining their original expression, cloning the reference, or smiling as configured).
  - Render with stunning clarity and detail as if shot with a high-end professional camera and lighting setup.
  - Ensure all identity source subjects and items (like vehicles or props) are fully integrated into the scene with matching lighting and realistic shadows.
  - ${backgroundInstruction}
  - ${lightingDirective}
  
  MASTER DECENCY LOCK:
  - Ensure all attire is high-coverage, modest, and professional. No nudity or revealing clothing.
  - NAVEL COVERAGE (STRICT): For Indian cultural attire (Sarees, Lehengas, etc.), you are strictly forbidden from revealing the subject's navel, but if navel is covered under a see through saree or similar, it is fine. Ensure the midriff is modestly covered.
  - STANDALONE BLOUSE PROHIBITION: Never render a subject wearing only a saree blouse; it must be a full professional ensemble.
  - CLEAVAGE LOCK: No visible cleavage; Slight cleavage is fine; ensure high-coverage necklines.

  FINAL NEGATIVE REINFORCEMENT (ABSOLUTE):
  - NO STUDIO GEAR: You are STRICTLY FORBIDDEN from rendering any softboxes, light stands, strobes, reflectors, or cameras. The background must be 100% clean.
  - NO ATTIRE LEAKAGE: Each subject's attire must be isolated. Do NOT apply "sub-4's" dress to "sub-2" unless instructed.
  - NO ORIGINAL CLOTHING: Do NOT use the subjects' original clothing from their source images unless explicitly told to "keep original clothes". You MUST transform their attire to the requested theme.
  - NO POSE DEVIATION: Adding a pet MUST NOT change the human poses.
  - NO LEAKAGE OF THE REFERENCE MODELS: The identities MUST come 100% and exclusively from the "IDENTITY SOURCE" images. You are strictly forbidden from allowing the appearance of the models in the style reference to influence the facial features of the subjects. Any leakage of the reference models' faces into the final output is a failure of the protocol.
  
  GLOBAL DIRECTIVE ISOLATION (ATMOSPHERIC SUPPLEMENT ONLY):
  - The "SCENE THEME DIRECTIVE" is for setting and atmosphere (e.g., "Taj Mahal at night") ONLY.
  - It is STRICTLY FORBIDDEN to use a scene directive as a justification to ignore attire, jewelry, or grooming instructions provided for individual subjects.
  - If a GLOBAL instruction mentions "expression" or "mood", apply it only to the face.
  - All subjects MUST be rendered in the specific attire defined by their SNIPPETS or the group reference, regardless of whether that attire "clashes" with the background scene theme.
  
  ${snippetsInstructionBlock}
  
  ${
    options.textOnlyDirective
      ? `SCENE THEME DIRECTIVE (BACKGROUND & ATMOSPHERE ONLY): ${options.textOnlyDirective}`
      : ""
  }
  
  Generate exactly one high-quality professional group portrait containing all ${
    members.length
  } identity source subjects with 100% identity fidelity. NO REFERENCE FIGURES ALLOWED. NO DUPLICATE SOURCE SUBJECTS ALLOWED.`;

  const parts = members
    .map((m, idx) => [
      { text: `IDENTITY SOURCE FOR PERSON ${idx + 1}:` },
      { inlineData: { mimeType: m.image.file.type, data: m.image.base64 } }
    ])
    .flat();

  if (styleRef) {
    parts.push({
      text: "AESTHETIC & ATTIRE STYLE REFERENCE (POSE & COMPOSITION GUIDE ONLY):"
    });
    parts.push({
      inlineData: { mimeType: styleRef.file.type, data: styleRef.base64 }
    });
    parts.push({
      text: "CRITICAL: YOU MUST COMPLETELY REPLACE EVERY PERSON IN THE ABOVE REFERENCE IMAGE WITH THE UNIQUE IDENTITIES PROVIDED IN THE 'IDENTITY SOURCE' IMAGES. ZERO LEAKAGE ALLOWED."
    });
  }
  if (bgRef) {
    parts.push({ text: "SCENE LOCATION REFERENCE:" });
    parts.push({
      inlineData: { mimeType: bgRef.file.type, data: bgRef.base64 }
    });
  }

  parts.push({ text: prompt });

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("GENERATION_TIMEOUT")), 180000)
  );

  try {
    const generationPromise = (async () => {
      let gemini_uri = ENDPOINTS.gemini.index.uri;

      // 1. Send it as an application/json payload instead of FormData
      const res = await fetch(gemini_uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          gemini_action: "generateGroupPortrait",
          parts: parts, // Keeps your array structure completely intact!
          aspectRatio: apiAspectRatio,
          imageSize: is4K ? "4K" : "2K"
        })
      });

      // This handles standard non-200 server failures if they happen
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP_ERROR_${res.status}`);
      }

      // 1. CRITICAL: Unpack the JSON string stream exactly once
      const data = await res.json();

      // 2. CHECK FOR BOTH GOOGLE & RUBY TIMEOUT ERRORS INSIDE THE 200 OK PAYLOAD
      if (data.error) {
        // Standardize the error message into a string variable
        const errMsg =
          typeof data.error === "object" ? data.error.message : data.error;
        // console.log(`Backend/Gemini returned an explicit error tracking payload: ${errMsg}`);

        // Track 1: Handle Google's 503 Overloaded Error
        if (
          errMsg.includes("overloaded") ||
          errMsg.includes("demand") ||
          errMsg.includes("503")
        ) {
          alert("MODEL_OVERLOADED! Please retry after sometime.");
          throw new Error("MODEL_OVERLOADED");
        }

        // Track 2: Handle Ruby's low-level Net::ReadTimeout ("execution expired") or 504 errors
        if (
          errMsg.includes("expired") ||
          errMsg.includes("timeout") ||
          errMsg.includes("504")
        ) {
          alert(
            "SERVER_TIMEOUT! Google's image generation took too long to read. Please retry after sometime."
          );
          throw new Error("SERVER_TIMEOUT");
        }

        // Track 3: General Fallback for other errors
        alert(`Error Encountered: ${errMsg}`);
        throw new Error(errMsg || "UNKNOWN_GENERATION_ERROR");
      }

      // 3. SUCCESS PATH: Return the clean data object safely to Promise.race

      return data;
    })(); // Invoked immediately as an async IIFE promise

    const response = await Promise.race([generationPromise, timeoutPromise]);

    const text = response.candidates ? true : false;
    if (text) {
      for (const part of response.candidates[0].content.parts || []) {
        if (part.inlineData) {
          const imageUrl = `data:image/png;base64,${part.inlineData.data}`;
          const usage = response.usageMetadata || {
            promptTokenCount: 0,
            candidatesTokenCount: 0,
            totalTokenCount: 0
          };
          return {
            imageUrl,
            usage: {
              promptTokens: usage.promptTokenCount,
              candidatesTokens: usage.candidatesTokenCount,
              totalTokens: usage.totalTokenCount,
              tokenCost:
                (usage.promptTokenCount / 1000000) * 1.25 +
                (usage.candidatesTokenCount / 1000000) * 5.0,
              imageCost: 0.03,
              estimatedCost:
                (usage.promptTokenCount / 1000000) * 1.25 +
                (usage.candidatesTokenCount / 1000000) * 5.0 +
                0.03
            }
          };
        }
      }
    } else {
      alert(
        `Something went wrong!. Image not generated. Most probable reason is due to safety/decency of reference attires! Kindly Check and try again.`
      );
      return;
    }

    const noImageReason = extractNoImageReason(response);
    alert(noImageReason);
    // throw new Error(noImageReason);
  } catch (error) {
    if (error.message.includes("Requested entity was not found")) {
      throw new Error("API_KEY_RESET_REQUIRED");
    }
    throw error;
  }
};
